import React, { useEffect, useState } from "react";
import LayoutContainer from "../base/LayoutContainer";
import axios from "axios";
import { Container, Row, Col, Spinner, Card, Button } from "react-bootstrap";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import initSqlJs from "sql.js";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  BubbleController,
  RadialLinearScale,
} from "chart.js";
import { Bar, Line, Bubble } from "react-chartjs-2";
import "./ReadingStats.css";
import { SITE_NAME } from "../site";
import BackHome from "../themes/BackHome";
import BookProgress from "./BookProgress";
import C from "./palette";

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  BubbleController,
  RadialLinearScale,
  Title,
  Tooltip,
  Legend
);

const READERS = [["kulfi", "Kulfi"], ["debu", "Debu"]];

// How far through a book you are: furthest reference page reached over the book's length.
// NOT distinct-pages-logged / length -- pages that never produced a stat row would understate
// position (read to page 189 of 462 is 41% in, even if only 80 pages logged a row).
// Same running-max formula the progress chart uses (see BookProgress.js).
const completionPct = (book) => {
  if (!book || !(book.totalPages > 0)) return 0;
  return Math.min(100, Math.round((Math.max(book.currentPage || 0, 0) / book.totalPages) * 100));
};

const ReadingStats = () => {
  // Reader comes from ?reader=debu so the choice is linkable; anything else is kulfi.
  const [reader, setReader] = useState(() =>
    new URLSearchParams(window.location.search).get("reader") === "debu" ? "debu" : "kulfi"
  );
  const pickReader = (r) => {
    setReader(r);
    const url = new URL(window.location.href);
    if (r === "kulfi") url.searchParams.delete("reader");
    else url.searchParams.set("reader", r);
    window.history.replaceState(null, "", url);
  };
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedBook, setSelectedBook] = useState(null);
  const [selectedChart, setSelectedChart] = useState("summary");
  const [heatmapRectSize] = useState(20);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [stats, setStats] = useState({
    totalBooks: 0,
    booksPerYear: [],
    topAuthors: [],
    weeklyStats: [],
    readingByDay: [],
    longestBooks: [],
    mostTimeBooks: [],
    fastestBooks: [],
    slowestBooks: [],
    allBooks: [],
    progressBooks: [],
    progressRows: [],
    bubbleChartData: [],
    calendarData: [],
    calendarPagesData: [],
  });

  useEffect(() => {
    const loadDatabase = async () => {
      setLoading(true);
      setError(null);
      setSelectedBook(null);
      try {
        const response = await axios.get(
          `https://rohitkulky.com/base/api/book-stats${reader === "debu" ? "?reader=debu" : ""}`
        );
        const SQL = await initSqlJs({
          locateFile: (file) => `${process.env.PUBLIC_URL}/${file}`,
        });

        const base64Data = response.data;
        const binaryString = atob(base64Data);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }

        const db = new SQL.Database(bytes);

        // Page numbers depend on the device's screen/layout. Shadow `page_stat_data` and `book` with
        // temp tables (temp wins name lookup) so every query below sees pages rescaled to each book's
        // own stored length: books keep their real relative size, but one book read on two screens
        // lands on a single scale. Rows whose page exceeds their own total_pages are self-contradictory
        // (bad layout info), and so are layouts more than 4x off the stored length, so those rows are left
        // as recorded (ep = 1) instead of being blown up. `ep` = normalized pages one row is worth (for counting "pages read").
        db.run(`CREATE TEMP TABLE page_stat_data AS
          SELECT p.id_book, p.start_time, p.duration,
            CASE WHEN p.total_pages > 0 AND n.pages > 0 AND p.page <= p.total_pages AND p.total_pages BETWEEN n.pages * 0.25 AND n.pages * 4
                 THEN MAX(1, MIN(n.pages, CAST(ROUND(p.page * 1.0 / p.total_pages * n.pages) AS INTEGER)))
                 ELSE p.page END AS page,
            COALESCE(NULLIF(n.pages, 0), p.total_pages) AS total_pages,
            CASE WHEN p.total_pages > 0 AND n.pages > 0 AND p.page <= p.total_pages AND p.total_pages BETWEEN n.pages * 0.25 AND n.pages * 4 THEN n.pages * 1.0 / p.total_pages ELSE 1.0 END AS ep
          FROM main.page_stat_data p
          LEFT JOIN main.book n ON n.id = p.id_book`);
        db.run(`CREATE TEMP TABLE book AS
          SELECT b.id, b.title, b.authors, b.pages, b.total_read_time, b.last_open, b.highlights, b.notes, b.md5,
            (SELECT COUNT(DISTINCT p.page) FROM temp.page_stat_data p WHERE p.id_book = b.id) AS total_read_pages
          FROM main.book b`);

        // Query all statistics (filter out books with less than 10 pages)
        const totalBooksResult = db.exec(
          "SELECT COUNT(*) as count FROM book WHERE total_read_pages > 3 AND pages >= 10"
        );
        const totalBooks = totalBooksResult[0]?.values[0][0] || 0;

        const booksPerYearResult = db.exec(`
          SELECT strftime('%Y', last_open, 'unixepoch') as year, COUNT(*) as count 
          FROM book WHERE last_open IS NOT NULL AND total_read_pages > 3 AND pages >= 10
          GROUP BY year ORDER BY year DESC LIMIT 10
        `);
        const booksPerYear =
          booksPerYearResult[0]?.values.map((row) => ({
            year: row[0],
            count: row[1],
          })) || [];

        const weeklyStatsResult = db.exec(`
          SELECT strftime('%Y-W%W', p.start_time, 'unixepoch') as week,
            CAST(ROUND(SUM(p.ep)) AS INTEGER) as pages, SUM(p.duration) / 3600.0 as hours
          FROM page_stat_data p GROUP BY week ORDER BY week DESC LIMIT 12
        `);
        const weeklyStats =
          weeklyStatsResult[0]?.values
            .map((row) => ({
              week: row[0],
              pages: row[1],
              hours: Math.round(row[2] * 10) / 10,
            }))
            .reverse() || [];

        const topAuthorsResult = db.exec(`
          SELECT authors, COUNT(*) as count FROM book 
          WHERE authors IS NOT NULL AND authors != 'N/A' AND total_read_pages > 3 AND pages >= 10
          GROUP BY authors ORDER BY count DESC LIMIT 10
        `);
        const topAuthors =
          topAuthorsResult[0]?.values.map((row) => ({
            author: row[0],
            count: row[1],
          })) || [];

        const totalTimeResult = db.exec(
          `SELECT SUM(total_read_time) as total_seconds FROM book WHERE total_read_pages > 3 AND pages >= 10`
        );
        const totalReadingTimeHours = Math.round(
          (totalTimeResult[0]?.values[0][0] || 0) / 3600
        );

        const totalPagesResult = db.exec(
          `SELECT SUM(total_read_pages) as total FROM book WHERE total_read_pages > 3 AND pages >= 10`
        );
        const totalPagesRead = totalPagesResult[0]?.values[0][0] || 0;

        const avgSpeed =
          totalReadingTimeHours > 0
            ? Math.round(totalPagesRead / totalReadingTimeHours)
            : 0;

        const longestBooksResult = db.exec(`
          SELECT b.title, b.authors, b.total_read_pages, b.pages, MAX(p.page) as current_page
          FROM book b LEFT JOIN page_stat_data p ON b.id = p.id_book
          WHERE b.total_read_pages > 3 AND b.pages >= 10
          GROUP BY b.id
          ORDER BY b.total_read_pages DESC LIMIT 5
        `);
        const longestBooks =
          longestBooksResult[0]?.values.map((row) => ({
            title: row[0],
            author: row[1],
            pagesRead: row[2],
            totalPages: row[3],
            currentPage: row[4] || 0,
          })) || [];

        const mostTimeResult = db.exec(`
          SELECT title, authors, total_read_time, total_read_pages FROM book 
          WHERE total_read_pages > 3 AND pages >= 10
          ORDER BY total_read_time DESC LIMIT 5
        `);
        const mostTimeBooks =
          mostTimeResult[0]?.values.map((row) => ({
            title: row[0],
            author: row[1],
            timeHours: Math.round((row[2] / 3600) * 10) / 10,
            pages: row[3],
          })) || [];

        const dayOfWeekResult = db.exec(`
          SELECT CASE CAST(strftime('%w', last_open, 'unixepoch') AS INTEGER)
              WHEN 0 THEN 'Sunday' WHEN 1 THEN 'Monday' WHEN 2 THEN 'Tuesday'
              WHEN 3 THEN 'Wednesday' WHEN 4 THEN 'Thursday' WHEN 5 THEN 'Friday'
              WHEN 6 THEN 'Saturday' END as day,
            AVG(total_read_time) / 3600.0 as avg_hours
          FROM book WHERE total_read_pages > 3 AND last_open IS NOT NULL AND pages >= 10
          GROUP BY strftime('%w', last_open, 'unixepoch')
          ORDER BY CAST(strftime('%w', last_open, 'unixepoch') AS INTEGER)
        `);
        const readingByDay =
          dayOfWeekResult[0]?.values.map((row) => ({
            day: row[0],
            avgHours: Math.round(row[1] * 10) / 10,
          })) || [];

        const streakResult = db.exec(`
          SELECT DISTINCT date(datetime(last_open, 'unixepoch', '-8 hours')) as day FROM book 
          WHERE total_read_pages > 3 AND last_open IS NOT NULL AND pages >= 10
          ORDER BY day DESC
        `);
        let currentStreak = 0,
          longestStreak = 0,
          tempStreak = 0;
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);

        if (streakResult[0]?.values) {
          let lastDate = null;
          streakResult[0].values.forEach((row, i) => {
            const dateStr = row[0];
            const date = new Date(dateStr + "T00:00:00");
            if (i === 0) {
              const todayStr = today.toISOString().split("T")[0];
              const yesterdayStr = yesterday.toISOString().split("T")[0];
              if (dateStr === todayStr || dateStr === yesterdayStr) {
                currentStreak = 1;
                tempStreak = 1;
              }
            } else if (lastDate) {
              const prevDay = new Date(lastDate);
              prevDay.setDate(prevDay.getDate() - 1);
              const prevDayStr = prevDay.toISOString().split("T")[0];
              if (dateStr === prevDayStr) {
                tempStreak++;
                if (i <= currentStreak) currentStreak = tempStreak;
              } else {
                if (tempStreak > longestStreak) longestStreak = tempStreak;
                tempStreak = 1;
              }
            }
            lastDate = date;
          });
          if (tempStreak > longestStreak) longestStreak = tempStreak;
        }

        const momentumResult = db.exec(`
          SELECT 
            SUM(CASE WHEN last_open >= strftime('%s', 'now', '-7 days') THEN total_read_time ELSE 0 END) as last_week,
            SUM(CASE WHEN last_open >= strftime('%s', 'now', '-14 days') AND last_open < strftime('%s', 'now', '-7 days') THEN total_read_time ELSE 0 END) as prev_week
          FROM book WHERE total_read_pages > 3
        `);
        const lastWeekTime = momentumResult[0]?.values[0][0] || 0;
        const prevWeekTime = momentumResult[0]?.values[0][1] || 0;
        const weekChange =
          prevWeekTime > 0
            ? ((lastWeekTime - prevWeekTime) / prevWeekTime) * 100
            : 0;

        const fastestBooksResult = db.exec(`
          SELECT title, authors, CAST(total_read_pages AS REAL) / (total_read_time / 60.0) as ppm
          FROM book WHERE total_read_time > 600 AND total_read_pages > 10
          ORDER BY ppm DESC LIMIT 5
        `);
        const fastestBooks =
          fastestBooksResult[0]?.values.map((row) => ({
            title: row[0],
            author: row[1],
            ppm: Math.round(row[2] * 10) / 10,
          })) || [];

        const slowestBooksResult = db.exec(`
          SELECT title, authors, CAST(total_read_pages AS REAL) / (total_read_time / 60.0) as ppm
          FROM book WHERE total_read_time > 600 AND total_read_pages > 10
          ORDER BY ppm ASC LIMIT 5
        `);
        const slowestBooks =
          slowestBooksResult[0]?.values.map((row) => ({
            title: row[0],
            author: row[1],
            ppm: Math.round(row[2] * 10) / 10,
          })) || [];

        const allBooksResult = db.exec(`
          SELECT b.id, b.title, b.authors, b.total_read_pages, b.pages, b.total_read_time,
            b.last_open, b.highlights, b.notes,
            MAX(p.page) as current_page
          FROM book b LEFT JOIN page_stat_data p ON b.id = p.id_book
          WHERE b.total_read_pages > 3 AND b.pages >= 10
          GROUP BY b.id
          ORDER BY b.last_open DESC
        `);
        const allBooks =
          allBooksResult[0]?.values.map((row) => ({
            id: row[0],
            title: row[1],
            author: row[2],
            pagesRead: row[3],
            totalPages: row[4],
            timeSeconds: row[5],
            lastOpen: row[6],
            highlights: row[7] || 0,
            notes: row[8] || 0,
            currentPage: row[9] || 0,
          })) || [];

        const bubbleDataResult = db.exec(`
          SELECT b.id, b.title, b.authors, b.pages, b.total_read_time / 3600.0 as time_hours,
            COUNT(DISTINCT date(datetime(p.start_time, 'unixepoch', '-8 hours'))) as sessions
          FROM book b LEFT JOIN page_stat_data p ON b.id = p.id_book
          WHERE b.total_read_pages > 3 AND b.pages >= 10 AND b.total_read_time > 0
          GROUP BY b.id HAVING sessions > 1 ORDER BY sessions DESC LIMIT 50
        `);
        const bubbleChartData =
          bubbleDataResult[0]?.values.map((row) => ({
            id: row[0],
            title: row[1],
            author: row[2],
            pages: row[3],
            timeHours: Math.round(row[4] * 10) / 10,
            sessions: row[5],
          })) || [];

        const calendarDataResult = db.exec(`
          SELECT date(datetime(p.start_time, 'unixepoch', '-8 hours')) as date,
            SUM(p.duration) / 3600.0 as hours, COUNT(DISTINCT p.id_book) as books
          FROM page_stat_data p GROUP BY date ORDER BY date ASC
        `);
        const calendarData =
          calendarDataResult[0]?.values.map((row) => ({
            date: row[0],
            hours: Math.round(row[1] * 10) / 10,
            books: row[2],
          })) || [];

        const calendarPagesDataResult = db.exec(`
          SELECT date(datetime(p.start_time, 'unixepoch', '-8 hours')) as date, CAST(ROUND(SUM(p.ep)) AS INTEGER) as pages
          FROM page_stat_data p GROUP BY date ORDER BY date ASC
        `);
        const calendarPagesData =
          calendarPagesDataResult[0]?.values.map((row) => ({
            date: row[0],
            pages: row[1],
          })) || [];

        // Deep insights queries
        const engagementPatternsResult = db.exec(`
          SELECT b.title, COUNT(p.id_book) as sessions,
            ROUND(AVG(p.duration) / 60.0, 2) as avg_session_min,
            ROUND(SUM(p.duration) / 3600.0, 2) as total_hours
          FROM page_stat_data p
          JOIN book b ON p.id_book = b.id
          GROUP BY p.id_book, b.title
          HAVING COUNT(p.id_book) > 10
          ORDER BY AVG(p.duration) DESC LIMIT 10
        `);
        const engagementPatterns =
          engagementPatternsResult[0]?.values.map((row) => ({
            title: row[0],
            sessions: row[1],
            avgSessionMin: row[2],
            totalHours: row[3],
          })) || [];

        const sessionClusteringResult = db.exec(`
          SELECT 
            CASE 
              WHEN duration < 30 THEN 'Quick (<30s)'
              WHEN duration < 60 THEN 'Short (30-60s)'
              WHEN duration < 120 THEN 'Medium (1-2min)'
              WHEN duration < 300 THEN 'Long (2-5min)'
              ELSE 'Extended (>5min)'
            END as session_type,
            COUNT(*) as count,
            ROUND(COUNT(*) * 100.0 / (SELECT COUNT(*) FROM page_stat_data), 2) as percentage
          FROM page_stat_data
          GROUP BY session_type
          ORDER BY COUNT(*) DESC
        `);
        const sessionClustering =
          sessionClusteringResult[0]?.values.map((row) => ({
            type: row[0],
            count: row[1],
            percentage: row[2],
          })) || [];

        const circadianPatternsResult = db.exec(`
          SELECT strftime('%H', datetime(start_time, 'unixepoch', '-8 hours')) as hour,
            COUNT(*) as sessions,
            ROUND(SUM(duration) / 3600.0, 2) as hours,
            ROUND(AVG(duration), 1) as avg_duration
          FROM page_stat_data
          GROUP BY hour
          ORDER BY avg_duration DESC LIMIT 10
        `);
        const circadianPatterns =
          circadianPatternsResult[0]?.values.map((row) => ({
            hour: row[0],
            sessions: row[1],
            hours: row[2],
            avgDuration: row[3],
          })) || [];

        const readingMomentumResult = db.exec(`
          WITH daily_reading AS (
            SELECT DISTINCT date(datetime(start_time, 'unixepoch', '-8 hours')) as reading_date,
              id_book, b.title
            FROM page_stat_data p
            JOIN book b ON p.id_book = b.id
          )
          SELECT title, COUNT(DISTINCT reading_date) as days_active,
            ROUND((julianday(MAX(reading_date)) - julianday(MIN(reading_date))), 1) as span_days
          FROM daily_reading
          GROUP BY id_book, title
          HAVING COUNT(DISTINCT reading_date) > 3
          ORDER BY COUNT(DISTINCT reading_date) DESC LIMIT 10
        `);
        const readingMomentum =
          readingMomentumResult[0]?.values.map((row) => ({
            title: row[0],
            daysActive: row[1],
            spanDays: row[2],
          })) || [];

        const consistencyResult = db.exec(`
          WITH daily_stats AS (
            SELECT date(datetime(start_time, 'unixepoch', '-8 hours')) as reading_date,
              COUNT(*) as daily_sessions,
              SUM(duration) as daily_duration
            FROM page_stat_data
            GROUP BY reading_date
          )
          SELECT 
            COUNT(*) as total_days,
            ROUND(AVG(daily_sessions), 2) as avg_sessions,
            ROUND(AVG(daily_duration) / 60.0, 2) as avg_minutes,
            MAX(daily_sessions) as max_sessions,
            ROUND(100.0 * COUNT(DISTINCT reading_date) / 
              (julianday(MAX(reading_date)) - julianday(MIN(reading_date)) + 1), 2) as consistency_score
          FROM daily_stats
        `);
        const consistency = consistencyResult[0]?.values[0] || [0, 0, 0, 0, 0];

        // Advanced analyses
        const sessionClusteringAdvResult = db.exec(`
          WITH session_features AS (
            SELECT id_book, start_time, duration,
              strftime('%H', datetime(start_time, 'unixepoch', '-8 hours')) as hour,
              CASE WHEN duration > 500 THEN 1 ELSE 0 END as deep_read
            FROM page_stat_data
          )
          SELECT 
            CASE 
              WHEN CAST(hour AS INTEGER) BETWEEN 6 AND 11 THEN 'Morning'
              WHEN CAST(hour AS INTEGER) BETWEEN 12 AND 17 THEN 'Afternoon'
              WHEN CAST(hour AS INTEGER) BETWEEN 18 AND 23 THEN 'Evening'
              ELSE 'Night'
            END as time_bucket,
            ROUND(AVG(duration), 1) as avg_duration,
            COUNT(*) as sessions,
            SUM(deep_read) as deep_sessions,
            ROUND(SUM(deep_read) * 100.0 / COUNT(*), 1) as deep_pct
          FROM session_features
          GROUP BY time_bucket
          ORDER BY avg_duration DESC
        `);
        const sessionClustersAdv =
          sessionClusteringAdvResult[0]?.values.map((row) => ({
            timeBucket: row[0],
            avgDuration: row[1],
            sessions: row[2],
            deepSessions: row[3],
            deepPct: row[4],
          })) || [];

        const temporalHabitsResult = db.exec(`
          WITH daily_cycles AS (
            SELECT 
              strftime('%w', datetime(start_time, 'unixepoch', '-8 hours')) as weekday,
              CAST(strftime('%H', datetime(start_time, 'unixepoch', '-8 hours')) AS INTEGER) as hour
            FROM page_stat_data
          )
          SELECT 
            CASE CAST(weekday AS INTEGER)
              WHEN 0 THEN 'Sunday' WHEN 1 THEN 'Monday' WHEN 2 THEN 'Tuesday'
              WHEN 3 THEN 'Wednesday' WHEN 4 THEN 'Thursday' WHEN 5 THEN 'Friday'
              WHEN 6 THEN 'Saturday'
            END as day,
            ROUND(AVG(hour), 1) as avg_hour,
            COUNT(*) as sessions
          FROM daily_cycles
          GROUP BY weekday
          ORDER BY CAST(weekday AS INTEGER)
        `);
        const temporalHabits =
          temporalHabitsResult[0]?.values.map((row) => ({
            day: row[0],
            avgHour: row[1],
            sessions: row[2],
          })) || [];

        const authorStyleResult = db.exec(`
          WITH author_sessions AS (
            SELECT b.authors, p.duration
            FROM page_stat_data p
            JOIN book b ON p.id_book = b.id
            WHERE b.authors IS NOT NULL AND b.authors != 'N/A'
          )
          SELECT 
            authors,
            ROUND(AVG(duration), 1) as mean_duration,
            COUNT(*) as sessions,
            ROUND(AVG(duration) * 1.0 / NULLIF(
              (SELECT AVG(duration) FROM page_stat_data), 0
            ), 2) as relative_pace
          FROM author_sessions
          GROUP BY authors
          HAVING COUNT(*) > 20
          ORDER BY relative_pace DESC
          LIMIT 10
        `);
        const authorStyles =
          authorStyleResult[0]?.values.map((row) => ({
            author: row[0],
            meanDuration: row[1],
            sessions: row[2],
            relativePace: row[3],
          })) || [];

        const completionAnomaliesResult = db.exec(`
          WITH book_stats AS (
            SELECT id, title, pages, total_read_pages, total_read_time,
              ROUND((total_read_pages * 1.0 / NULLIF(pages, 0)) * 100, 1) as comp_pct,
              ROUND(total_read_time * 1.0 / NULLIF(pages, 0), 2) as sec_per_page
            FROM book
            WHERE pages > 0 AND total_read_pages > 10
          ),
          stats_with_avg AS (
            SELECT *,
              AVG(sec_per_page) OVER() as avg_sec_page
            FROM book_stats
          )
          SELECT title, comp_pct, sec_per_page,
            ROUND(sec_per_page - avg_sec_page, 2) as time_anomaly,
            CASE 
              WHEN comp_pct > 80 AND sec_per_page < 0.5 * avg_sec_page THEN 'Fast Completion'
              WHEN comp_pct < 30 AND sec_per_page > 1.5 * avg_sec_page THEN 'Slow Abandonment'
              ELSE 'Normal'
            END as pattern
          FROM stats_with_avg
          WHERE ABS(sec_per_page - avg_sec_page) > avg_sec_page * 0.5
          ORDER BY ABS(time_anomaly) DESC
          LIMIT 10
        `);
        const completionAnomalies =
          completionAnomaliesResult[0]?.values.map((row) => ({
            title: row[0],
            compPct: row[1],
            secPerPage: row[2],
            timeAnomaly: row[3],
            pattern: row[4],
          })) || [];

        // Chart-friendly queries
        const paceOverTimeResult = db.exec(`
          SELECT strftime('%Y-W%W', datetime(start_time, 'unixepoch')) as week,
            ROUND(SUM(duration) / SUM(ep), 1) as avg_sec_per_page,
            COUNT(*) as sessions,
            ROUND(SUM(duration) / 3600.0, 2) as total_hours
          FROM page_stat_data
          GROUP BY week
          ORDER BY week
        `);
        const paceOverTime =
          paceOverTimeResult[0]?.values.map((row) => ({
            week: row[0],
            avgSecPerPage: row[1],
            sessions: row[2],
            totalHours: row[3],
          })) || [];

        const engagementVsPaceResult = db.exec(`
          WITH pace AS (
            SELECT id_book, SUM(duration) / SUM(ep) as avg_sec_per_page
            FROM page_stat_data
            GROUP BY id_book
          )
          SELECT b.title, b.authors,
            (COALESCE(b.notes,0) + COALESCE(b.highlights,0)) * 1.0 / NULLIF(b.pages,0) as engagement_per_page,
            p.avg_sec_per_page,
            b.total_read_time / 3600.0 as hours
          FROM book b
          LEFT JOIN pace p ON p.id_book = b.id
          WHERE b.pages > 0 AND b.total_read_time > 0
          ORDER BY engagement_per_page DESC
          LIMIT 20
        `);
        const engagementVsPace =
          engagementVsPaceResult[0]?.values.map((row) => ({
            title: row[0],
            author: row[1],
            engagementPerPage: Math.round(row[2] * 1000) / 1000,
            avgSecPerPage: Math.round(row[3] * 10) / 10,
            hours: Math.round(row[4] * 10) / 10,
          })) || [];

        const timeOfDayHeatmapResult = db.exec(`
          SELECT strftime('%w', datetime(start_time, 'unixepoch', '-8 hours')) as weekday,
            strftime('%H', datetime(start_time, 'unixepoch', '-8 hours')) as hour,
            COUNT(*) as sessions,
            ROUND(SUM(duration) / 3600.0, 2) as total_hours
          FROM page_stat_data
          GROUP BY weekday, hour
          ORDER BY weekday, hour
        `);
        const timeOfDayHeatmap =
          timeOfDayHeatmapResult[0]?.values.map((row) => ({
            weekday: parseInt(row[0]),
            hour: parseInt(row[1]),
            sessions: row[2],
            totalHours: row[3],
          })) || [];

        const monthlyPaceResult = db.exec(`
          SELECT strftime('%Y-W%W', datetime(start_time, 'unixepoch')) as week,
            ROUND(SUM(duration) / SUM(ep), 1) as avg_sec_per_page,
            COUNT(*) as sessions
          FROM page_stat_data
          GROUP BY week
          ORDER BY week DESC
          LIMIT 12
        `);
        const monthlyPace =
          monthlyPaceResult[0]?.values.map((row) => ({
            week: row[0],
            avgSecPerPage: row[1],
            sessions: row[2],
          })) || [];

        // Additional insights from database analysis
        const sessionDurationCapResult = db.exec(`
          SELECT duration, COUNT(*) as count
          FROM page_stat_data
          GROUP BY duration
          ORDER BY count DESC
          LIMIT 10
        `);
        const sessionDurationCap =
          sessionDurationCapResult[0]?.values.map((row) => ({
            duration: row[0],
            count: row[1],
          })) || [];

        const readingBurstsResult = db.exec(`
          SELECT date(datetime(start_time, 'unixepoch', '-8 hours')) as date,
            COUNT(*) as sessions,
            b.title,
            SUM(duration) / 3600.0 as hours
          FROM page_stat_data p
          JOIN book b ON p.id_book = b.id
          GROUP BY date, p.id_book, b.title
          HAVING sessions > 30
          ORDER BY sessions DESC
          LIMIT 10
        `);
        const readingBursts =
          readingBurstsResult[0]?.values.map((row) => ({
            date: row[0],
            sessions: row[1],
            title: row[2],
            hours: Math.round(row[3] * 10) / 10,
          })) || [];

        const topRevisitedPagesResult = db.exec(`
          SELECT b.title, p.page,
            COUNT(*) as visit_count,
            SUM(p.duration) as total_seconds,
            ROUND(SUM(p.duration) / 60.0, 1) as total_minutes
          FROM page_stat_data p
          JOIN book b ON p.id_book = b.id
          GROUP BY p.id_book, p.page, b.title
          ORDER BY total_seconds DESC
          LIMIT 15
        `);
        const topRevisitedPages =
          topRevisitedPagesResult[0]?.values.map((row) => ({
            title: row[0],
            page: row[1],
            visitCount: row[2],
            totalSeconds: row[3],
            totalMinutes: row[4],
          })) || [];

        const bookDominanceResult = db.exec(`
          SELECT b.title, b.authors,
            COUNT(p.id_book) as sessions,
            b.total_read_time / 3600.0 as hours,
            b.total_read_pages as pages_read,
            ROUND(COUNT(p.id_book) * 100.0 / (SELECT COUNT(*) FROM page_stat_data), 1) as session_pct
          FROM book b
          LEFT JOIN page_stat_data p ON b.id = p.id_book
          GROUP BY b.id, b.title, b.authors
          ORDER BY sessions DESC
        `);
        const bookDominance =
          bookDominanceResult[0]?.values.map((row) => ({
            title: row[0],
            author: row[1],
            sessions: row[2],
            hours: Math.round(row[3] * 10) / 10,
            pagesRead: row[4],
            sessionPct: row[5],
          })) || [];

        const dataQualityResult = db.exec(`
          SELECT 
            COUNT(CASE WHEN p.page > b.pages THEN 1 END) as pages_exceeding_book_length,
            COUNT(CASE WHEN p.duration = 120 THEN 1 END) as sessions_at_cap,
            COUNT(CASE WHEN p.total_pages != b.pages THEN 1 END) as page_count_mismatches,
            COUNT(*) as total_sessions
          FROM main.page_stat_data p
          JOIN main.book b ON p.id_book = b.id
        `);
        const dataQuality = dataQualityResult[0]?.values[0] || [0, 0, 0, 0];

        // Currently Reading - books with activity in last 7 days
        const currentlyReadingResult = db.exec(`
          SELECT b.id, b.title, b.authors, b.total_read_pages, b.pages,
            b.total_read_time, b.last_open,
            MAX(p.page) as current_page,
            COUNT(DISTINCT CASE WHEN p.start_time >= strftime('%s', 'now', '-7 days')
              THEN date(datetime(p.start_time, 'unixepoch', '-8 hours')) END) as days_active_last_week
          FROM book b
          LEFT JOIN page_stat_data p ON b.id = p.id_book
          WHERE b.last_open >= strftime('%s', 'now', '-7 days')
            AND b.total_read_pages > 3 AND b.pages >= 10
          GROUP BY b.id, b.title, b.authors
          ORDER BY b.last_open DESC
          LIMIT 5
        `);
        const currentlyReading =
          currentlyReadingResult[0]?.values.map((row) => ({
            id: row[0],
            title: row[1],
            author: row[2],
            pagesRead: row[3],
            totalPages: row[4],
            timeSeconds: row[5],
            lastOpen: row[6],
            currentPage: row[7],
            daysActiveLastWeek: row[8],
          })) || [];

        // Last 2 Weeks Activity Summary
        const twoWeeksActivityResult = db.exec(`
          SELECT 
            COUNT(DISTINCT date(datetime(p.start_time, 'unixepoch', '-8 hours'))) as days_read,
            COUNT(*) as total_sessions,
            COUNT(DISTINCT p.id_book) as books_touched,
            ROUND(SUM(p.duration) / 3600.0, 1) as total_hours,
            ROUND(AVG(p.duration), 1) as avg_session_duration,
            CAST(ROUND(SUM(p.ep)) AS INTEGER) as total_pages
          FROM page_stat_data p
          WHERE p.start_time >= strftime('%s', 'now', '-14 days')
        `);
        const twoWeeksActivity = twoWeeksActivityResult[0]?.values[0] || [0, 0, 0, 0, 0, 0];

        // Books read in last 2 weeks with details
        const twoWeeksBooksResult = db.exec(`
          SELECT b.title, b.authors,
            COUNT(*) as sessions,
            ROUND(SUM(p.duration) / 3600.0, 1) as hours,
            COUNT(DISTINCT date(datetime(p.start_time, 'unixepoch', '-8 hours'))) as days_active
          FROM page_stat_data p
          JOIN book b ON p.id_book = b.id
          WHERE p.start_time >= strftime('%s', 'now', '-14 days')
          GROUP BY b.id, b.title, b.authors
          ORDER BY hours DESC
          LIMIT 5
        `);
        const twoWeeksBooks =
          twoWeeksBooksResult[0]?.values.map((row) => ({
            title: row[0],
            author: row[1],
            sessions: row[2],
            hours: row[3],
            daysActive: row[4],
          })) || [];

        // Week-over-week comparison
        const weekComparisonResult = db.exec(`
          SELECT 
            SUM(CASE WHEN p.start_time >= strftime('%s', 'now', '-7 days') THEN p.duration ELSE 0 END) / 3600.0 as week2_hours,
            SUM(CASE WHEN p.start_time >= strftime('%s', 'now', '-14 days') AND p.start_time < strftime('%s', 'now', '-7 days') THEN p.duration ELSE 0 END) / 3600.0 as week1_hours,
            SUM(CASE WHEN p.start_time >= strftime('%s', 'now', '-7 days') THEN p.ep ELSE 0 END) as week2_pages,
            SUM(CASE WHEN p.start_time >= strftime('%s', 'now', '-14 days') AND p.start_time < strftime('%s', 'now', '-7 days') THEN p.ep ELSE 0 END) as week1_pages
          FROM page_stat_data p
        `);
        const weekComparison = weekComparisonResult[0]?.values[0] || [0, 0, 0, 0];

        // Peak reading hours in last 2 weeks
        const twoWeeksPeakHoursResult = db.exec(`
          SELECT strftime('%H', datetime(start_time, 'unixepoch', '-8 hours')) as hour,
            COUNT(*) as sessions,
            ROUND(SUM(duration) / 3600.0, 1) as hours
          FROM page_stat_data
          WHERE start_time >= strftime('%s', 'now', '-14 days')
          GROUP BY hour
          ORDER BY sessions DESC
          LIMIT 3
        `);
        const twoWeeksPeakHours =
          twoWeeksPeakHoursResult[0]?.values.map((row) => ({
            hour: parseInt(row[0]),
            sessions: row[1],
            hours: row[2],
          })) || [];

        // Most engaged book (by highlights/notes) in last 2 weeks
        const twoWeeksMostEngagedResult = db.exec(`
          SELECT b.title, b.authors,
            COALESCE(b.highlights, 0) + COALESCE(b.notes, 0) as total_engagement,
            b.highlights, b.notes
          FROM book b
          WHERE b.last_open >= strftime('%s', 'now', '-14 days')
            AND (b.highlights > 0 OR b.notes > 0)
          ORDER BY total_engagement DESC
          LIMIT 1
        `);
        const twoWeeksMostEngaged = twoWeeksMostEngagedResult[0]?.values[0] 
          ? {
              title: twoWeeksMostEngagedResult[0].values[0][0],
              author: twoWeeksMostEngagedResult[0].values[0][1],
              totalEngagement: twoWeeksMostEngagedResult[0].values[0][2],
              highlights: twoWeeksMostEngagedResult[0].values[0][3],
              notes: twoWeeksMostEngagedResult[0].values[0][4],
            }
          : null;

        // Longest session in last 2 weeks
        const twoWeeksLongestSessionResult = db.exec(`
          SELECT b.title, MAX(p.duration) as max_duration,
            date(datetime(p.start_time, 'unixepoch', '-8 hours')) as session_date
          FROM page_stat_data p
          JOIN book b ON p.id_book = b.id
          WHERE p.start_time >= strftime('%s', 'now', '-14 days')
          GROUP BY b.id, b.title
          ORDER BY max_duration DESC
          LIMIT 1
        `);
        const twoWeeksLongestSession = twoWeeksLongestSessionResult[0]?.values[0]
          ? {
              title: twoWeeksLongestSessionResult[0].values[0][0],
              duration: twoWeeksLongestSessionResult[0].values[0][1],
              date: twoWeeksLongestSessionResult[0].values[0][2],
            }
          : null;

        // Daily activity for last 2 weeks
        const twoWeeksDailyResult = db.exec(`
          SELECT date(datetime(p.start_time, 'unixepoch', '-8 hours')) as date,
            ROUND(SUM(p.duration) / 3600.0, 1) as hours,
            CAST(ROUND(SUM(p.ep)) AS INTEGER) as sessions -- normalized pages read that day
          FROM page_stat_data p
          WHERE p.start_time >= strftime('%s', 'now', '-14 days')
          GROUP BY date
          ORDER BY date ASC
        `);
        const twoWeeksDaily =
          twoWeeksDailyResult[0]?.values.map((row) => ({
            date: row[0],
            hours: row[1],
            sessions: row[2],
          })) || [];

        // Hourly reading data for last 2 weeks (336 hours)
        // Cap each session at 120 seconds (2 minutes) to avoid inflated values
        const twoWeeksHourlyResult = db.exec(`
          SELECT 
            strftime('%Y-%m-%d %H:00:00', datetime(p.start_time, 'unixepoch', '-8 hours')) as hour_bucket,
            SUM(MIN(p.duration, 120)) / 3600.0 as hours
          FROM page_stat_data p
          WHERE p.start_time >= strftime('%s', 'now', '-14 days')
          GROUP BY hour_bucket
          ORDER BY hour_bucket ASC
        `);
        const twoWeeksHourly =
          twoWeeksHourlyResult[0]?.values.map((row) => ({
            hourBucket: row[0],
            hours: row[1],
          })) || [];

        const progressBooksResult = db.exec(`
          SELECT DISTINCT b.id, b.title, b.authors, b.pages
          FROM book b JOIN page_stat_data p ON b.id = p.id_book
          WHERE b.total_read_pages > 3 AND b.pages >= 10
          ORDER BY b.last_open DESC
        `);
        const progressBooks =
          progressBooksResult[0]?.values.map((row) => ({
            id: row[0], title: row[1], author: row[2], totalPages: row[3],
          })) || [];
        const progressRowsResult = db.exec(`
          SELECT p.id_book, date(datetime(p.start_time, 'unixepoch', '-8 hours')) as date, MAX(p.page),
            SUM(p.duration) / SUM(p.ep) as sec_per_page
          FROM page_stat_data p JOIN book b ON p.id_book = b.id
          WHERE b.total_read_pages > 3 AND b.pages >= 10
          GROUP BY p.id_book, date ORDER BY p.id_book, date
        `);
        const progressRows =
          progressRowsResult[0]?.values.map((row) => ({ bookId: row[0], date: row[1], maxPage: row[2], secPerPage: Math.round(row[3] * 10) / 10 })) || [];

        setStats({
          progressBooks,
          progressRows,
          totalBooks,
          booksPerYear,
          weeklyStats,
          topAuthors,
          totalReadingTimeHours,
          totalPagesRead,
          avgSpeed,
          longestBooks,
          mostTimeBooks,
          readingByDay,
          currentStreak,
          longestStreak,
          lastWeekTime: Math.round(lastWeekTime / 3600),
          weekChange: Math.round(weekChange),
          fastestBooks,
          slowestBooks,
          allBooks,
          bubbleChartData,
          calendarData,
          calendarPagesData,
          engagementPatterns,
          sessionClustering,
          circadianPatterns,
          readingMomentum,
          consistency: {
            totalDays: consistency[0],
            avgSessions: consistency[1],
            avgMinutes: consistency[2],
            maxSessions: consistency[3],
            consistencyScore: consistency[4],
          },
          sessionClustersAdv,
          temporalHabits,
          authorStyles,
          completionAnomalies,
          paceOverTime,
          engagementVsPace,
          timeOfDayHeatmap,
          monthlyPace,
          sessionDurationCap,
          readingBursts,
          topRevisitedPages,
          bookDominance,
          dataQuality: {
            pagesExceeding: dataQuality[0],
            sessionsAtCap: dataQuality[1],
            pageCountMismatches: dataQuality[2],
            totalSessions: dataQuality[3],
          },
          currentlyReading,
          twoWeeksActivity: {
            daysRead: twoWeeksActivity[0],
            totalSessions: twoWeeksActivity[1],
            booksTouched: twoWeeksActivity[2],
            totalHours: twoWeeksActivity[3],
            avgSessionDuration: twoWeeksActivity[4],
            totalPages: twoWeeksActivity[5],
          },
          twoWeeksBooks,
          weekComparison: {
            week2Hours: Math.round(weekComparison[0] * 10) / 10,
            week1Hours: Math.round(weekComparison[1] * 10) / 10,
            week2Pages: Math.round(weekComparison[2]),
            week1Pages: Math.round(weekComparison[3]),
          },
          twoWeeksPeakHours,
          twoWeeksMostEngaged,
          twoWeeksLongestSession,
          twoWeeksDaily,
          twoWeeksHourly,
        });

        setLoading(false);
      } catch (err) {
        console.error("Error loading database:", err);
        setError(err.message);
        setLoading(false);
      }
    };

    loadDatabase();
  }, [reader]);

  // Styled like the sidebar's section label + items so it reads as part of the menu.
  const readerSelector = (
    <div role="group" aria-label="Reader" style={{ marginBottom: "20px" }}>
      <div style={{ fontSize: "0.75rem", color: C.dim, fontWeight: "bold", marginBottom: "8px", textTransform: "uppercase", letterSpacing: "0.5px" }}>
        Reader:
      </div>
      {READERS.map(([id, label]) => (
        <div
          key={id}
          role="button"
          tabIndex={0}
          aria-pressed={reader === id}
          onClick={() => reader !== id && pickReader(id)}
          onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && reader !== id && pickReader(id)}
          style={{
            padding: "6px 10px",
            marginBottom: "2px",
            fontSize: "0.85rem",
            color: reader === id ? C.accent : C.muted,
            backgroundColor: reader === id ? C.accentBg : "transparent",
            borderLeft: reader === id ? "3px solid #007bff" : "3px solid transparent",
            cursor: reader === id ? "default" : "pointer",
            borderRadius: "4px",
          }}
        >
          {label}
        </div>
      ))}
    </div>
  );

  if (loading) {
    return (
      <LayoutContainer>
        <Container className="middleCenter">
          {readerSelector}
          <Spinner animation="grow" size="lg" />
          <p
            className="mt-3"
            style={{ color: C.text }}
          >
            Loading reading statistics...
          </p>
        </Container>
      </LayoutContainer>
    );
  }

  if (error) {
    return (
      <LayoutContainer>
        <Container className="middleCenter">
          {readerSelector}
          <p style={{ color: "red", textShadow: "0 0 2px white" }}>
            Error loading {reader === "debu" ? "Debu's" : "Kulfi's"} statistics: {error}
          </p>
        </Container>
      </LayoutContainer>
    );
  }

  // Chart configurations
  const booksPerYearData = {
    labels: stats.booksPerYear.map((item) => item.year),
    datasets: [
      {
        label: "Books Read",
        data: stats.booksPerYear.map((item) => item.count),
        backgroundColor: "rgba(75, 192, 192, 0.6)",
        borderColor: "rgba(75, 192, 192, 1)",
        borderWidth: 2,
      },
    ],
  };

  const weeklyTrendData = {
    labels: stats.weeklyStats?.map((item) => item.week) || [],
    datasets: [
      {
        label: "Ref. Pages Read",
        data: stats.weeklyStats?.map((item) => item.pages) || [],
        backgroundColor: "rgba(75, 192, 192, 0.6)",
        borderColor: "rgba(75, 192, 192, 1)",
        borderWidth: 2,
        tension: 0.4,
        yAxisID: "y",
      },
      {
        label: "Hours Read",
        data: stats.weeklyStats?.map((item) => item.hours) || [],
        backgroundColor: "rgba(153, 102, 255, 0.6)",
        borderColor: "rgba(153, 102, 255, 1)",
        borderWidth: 2,
        tension: 0.4,
        yAxisID: "y1",
      },
    ],
  };

  const topAuthorsData = {
    labels: stats.topAuthors.map((item) => item.author),
    datasets: [
      {
        label: "Books by Author",
        data: stats.topAuthors.map((item) => item.count),
        backgroundColor: [
          "rgba(255, 99, 132, 0.6)",
          "rgba(54, 162, 235, 0.6)",
          "rgba(255, 206, 86, 0.6)",
          "rgba(75, 192, 192, 0.6)",
          "rgba(153, 102, 255, 0.6)",
          "rgba(255, 159, 64, 0.6)",
          "rgba(199, 199, 199, 0.6)",
          "rgba(83, 102, 255, 0.6)",
          "rgba(255, 99, 255, 0.6)",
          "rgba(99, 255, 132, 0.6)",
        ],
        borderWidth: 2,
      },
    ],
  };

  const readingByDayData = {
    labels: stats.readingByDay?.map((item) => item.day) || [],
    datasets: [
      {
        label: "Avg Reading Time (hours)",
        data: stats.readingByDay?.map((item) => item.avgHours) || [],
        backgroundColor: "rgba(255, 159, 64, 0.6)",
        borderColor: "rgba(255, 159, 64, 1)",
        borderWidth: 2,
      },
    ],
  };

  const bubbleData = {
    datasets: [
      {
        label: "Books",
        data:
          stats.bubbleChartData?.map((book) => ({
            x: book.sessions,
            y: book.timeHours,
            r: Math.sqrt(book.pages) / 2,
            title: book.title,
            author: book.author,
            pages: book.pages,
          })) || [],
        backgroundColor: "rgba(75, 192, 192, 0.6)",
        borderColor: "rgba(75, 192, 192, 1)",
        borderWidth: 2,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top",
        labels: { color: C.text, font: { size: 14 } },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: { color: C.text },
        grid: { color: C.grid },
      },
      x: {
        ticks: { color: C.text },
        grid: { color: C.grid },
      },
    },
  };

  const weeklyTrendOptions = {
    ...chartOptions,
    interaction: { mode: "index", intersect: false },
    scales: {
      y: {
        type: "linear",
        display: true,
        position: "left",
        beginAtZero: true,
        title: {
          display: true,
          text: "Ref. Pages Read",
          color: C.text,
          font: { size: 12 },
        },
        ticks: { color: C.text },
        grid: { color: C.grid },
      },
      y1: {
        type: "linear",
        display: true,
        position: "right",
        beginAtZero: true,
        title: {
          display: true,
          text: "Hours Read",
          color: C.text,
          font: { size: 12 },
        },
        ticks: { color: C.text },
        grid: { drawOnChartArea: false },
      },
      x: {
        ticks: { color: C.text },
        grid: { color: C.grid },
      },
    },
  };

  const bubbleOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: function (context) {
            const data = context.raw;
            return [
              `${data.title}`,
              `Author: ${data.author}`,
              `Sessions: ${data.x}`,
              `Time: ${data.y}h`,
              `Pages: ${data.pages}`,
            ];
          },
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: "Reading Time (hours)",
          color: C.text,
          font: { size: 14 },
        },
        ticks: { color: C.text },
        grid: { color: C.grid },
      },
      x: {
        beginAtZero: true,
        title: {
          display: true,
          text: "Reading Sessions (days)",
          color: C.text,
          font: { size: 14 },
        },
        ticks: { color: C.text, stepSize: 1 },
        grid: { color: C.grid },
      },
    },
  };

  // New chart configurations
  const paceOverTimeData = {
    labels: stats.paceOverTime?.map((item) => item.week) || [],
    datasets: [
      {
        label: "Seconds per Ref. Page",
        data: stats.paceOverTime?.map((item) => item.avgSecPerPage) || [],
        backgroundColor: "rgba(255, 99, 132, 0.6)",
        borderColor: "rgba(255, 99, 132, 1)",
        borderWidth: 2,
        tension: 0.4,
        yAxisID: "y",
      },
      {
        label: "Total Hours",
        data: stats.paceOverTime?.map((item) => item.totalHours) || [],
        backgroundColor: "rgba(54, 162, 235, 0.6)",
        borderColor: "rgba(54, 162, 235, 1)",
        borderWidth: 2,
        tension: 0.4,
        yAxisID: "y1",
      },
    ],
  };

  const engagementScatterData = {
    datasets: [
      {
        label: "Books",
        data:
          stats.engagementVsPace?.map((book) => ({
            x: book.avgSecPerPage,
            y: book.engagementPerPage,
            r: Math.sqrt(book.hours) * 3,
            title: book.title,
            author: book.author,
            hours: book.hours,
          })) || [],
        backgroundColor: "rgba(156, 39, 176, 0.6)",
        borderColor: "rgba(156, 39, 176, 1)",
        borderWidth: 2,
      },
    ],
  };

  const engagementScatterOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: function (context) {
            const data = context.raw;
            return [
              `${data.title}`,
              `Author: ${data.author}`,
              `Pace: ${data.x}s/ref. page`,
              `Engagement: ${data.y}/ref. page`,
              `Time: ${data.hours}h`,
            ];
          },
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: "Engagement per Ref. Page (notes+highlights)",
          color: C.text,
          font: { size: 14 },
        },
        ticks: { color: C.text },
        grid: { color: C.grid },
      },
      x: {
        beginAtZero: true,
        title: {
          display: true,
          text: "Seconds per Ref. Page",
          color: C.text,
          font: { size: 14 },
        },
        ticks: { color: C.text },
        grid: { color: C.grid },
      },
    },
  };

  // Render 2-week hourly timeline (GitHub-style)
  const renderTwoWeekTimeline = () => {
    if (!stats.twoWeeksHourly) return null;

    // Create a map of hour buckets to values
    const hourMap = new Map();
    stats.twoWeeksHourly.forEach((item) => {
      hourMap.set(item.hourBucket, item.hours);
    });

    // Generate all hours for the past 2 weeks (336 hours = 14 days × 24 hours)
    const endDate = new Date();
    const startDate = new Date(endDate.getTime() - 14 * 24 * 60 * 60 * 1000);
    
    // Adjust startDate to the beginning of the day (midnight) and to the previous Sunday
    const adjustedStartDate = new Date(startDate);
    adjustedStartDate.setHours(0, 0, 0, 0);
    const dayOfWeek = adjustedStartDate.getDay();
    if (dayOfWeek !== 0) {
      adjustedStartDate.setDate(adjustedStartDate.getDate() - dayOfWeek);
    }
    
    const hours = [];
    let currentDate = new Date(adjustedStartDate);
    
    while (currentDate <= endDate) {
      // Format to match SQL output: 'YYYY-MM-DD HH:00:00'
      const year = currentDate.getFullYear();
      const month = String(currentDate.getMonth() + 1).padStart(2, '0');
      const day = String(currentDate.getDate()).padStart(2, '0');
      const hour = String(currentDate.getHours()).padStart(2, '0');
      const hourBucket = `${year}-${month}-${day} ${hour}:00:00`;
      
      const value = hourMap.get(hourBucket) || 0;
      hours.push({ 
        hourBucket, 
        value,
        date: new Date(currentDate),
        dayOfWeek: currentDate.getDay(),
        hour: currentDate.getHours()
      });
      currentDate.setHours(currentDate.getHours() + 1);
    }

    // Color scale based on reading duration (in hours)
    const getColor = (value) => {
      if (value === 0) return C.empty;
      if (value >= 0.5) return C.r5;  // 30+ minutes
      if (value >= 0.3) return C.r4;  // 18+ minutes
      if (value >= 0.2) return C.r3;  // 12+ minutes
      if (value >= 0.1) return C.r2;  // 6+ minutes
      if (value >= 0.05) return C.r1; // 3+ minutes
      return C.r0;                     // Any reading
    };

    // Group hours by day for display
    const days = [];
    for (let i = 0; i < hours.length; i += 24) {
      days.push(hours.slice(i, i + 24));
    }

    return (
      <Row className="mb-4">
        <Col md={12}>
          <Card className="chart-card">
            <Card.Body>
              <Card.Title>📅 Last 2 Weeks Reading Timeline</Card.Title>
              <Card.Text style={{ fontSize: "0.9rem", color: C.muted, marginBottom: "15px" }}>
                Hourly reading activity for the past 14 days. Each square represents 1 hour.
              </Card.Text>
              <div style={{ padding: "20px 0", overflowX: "auto", minWidth: "100%" }}>
                <div style={{ minWidth: "100%", display: "flex", flexDirection: "column", gap: "2px" }}>
                  {/* Hour legend */}
                  <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "2px" }}>
                    <div style={{ 
                      width: "80px",
                      minWidth: "80px",
                      flexShrink: 0
                    }} />
                    <div style={{ display: "flex", gap: "2px", flexShrink: 0, position: "relative" }}>
                      {Array.from({ length: 24 }, (_, hour) => {
                        const displayHour = hour === 0 ? "12a" : 
                                          hour < 12 ? `${hour}a` : 
                                          hour === 12 ? "12p" : 
                                          `${hour - 12}p`;
                        // Show label every 3 hours
                        const showLabel = hour % 3 === 0;
                        
                        return (
                          <div
                            key={hour}
                            style={{
                              width: "35px",
                              height: "15px",
                              flexShrink: 0,
                              fontSize: showLabel ? "0.6rem" : "0",
                              color: C.dim,
                              textAlign: "center",
                              fontWeight: "500"
                            }}
                          >
                            {showLabel ? displayHour : ""}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  
                  {days.map((day, dayIndex) => {
                    const dayDate = day[0].date;
                    const dayLabel = dayDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                    const dayName = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][dayDate.getDay()];
                    
                    return (
                      <div key={dayIndex} style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <div style={{ 
                          width: "80px",
                          minWidth: "80px",
                          fontSize: "0.7rem", 
                          color: C.muted, 
                          fontWeight: "bold",
                          textAlign: "right",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          flexShrink: 0
                        }}>
                          {dayName} {dayLabel}
                        </div>
                        <div style={{ display: "flex", gap: "2px", flexShrink: 0 }}>
                          {day.map((hour, hourIndex) => {
                            const color = getColor(hour.value);
                            const minutes = Math.round(hour.value * 60);
                            const displayTime = hour.hour === 0 ? "12 AM" : 
                                              hour.hour < 12 ? `${hour.hour} AM` : 
                                              hour.hour === 12 ? "12 PM" : 
                                              `${hour.hour - 12} PM`;
                            
                            return (
                              <div
                                key={hourIndex}
                                title={`${dayLabel} ${displayTime}: ${minutes} min`}
                                style={{
                                  width: "35px",
                                  height: "20px",
                                  backgroundColor: color,
                                  borderRadius: "5px",
                                  cursor: "pointer",
                                  transition: "all 0.2s",
                                  flexShrink: 0,
                                  fontSize: '0.4em',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  color: 'black'
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.transform = "scale(1.3)";
                                  e.currentTarget.style.zIndex = "10";
                                  e.currentTarget.style.boxShadow = C.glow;
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.transform = "scale(1)";
                                  e.currentTarget.style.zIndex = "1";
                                  e.currentTarget.style.boxShadow = "none";
                                }}
                              >
                                {color === C.empty ? '' : minutes}
                                </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
                
                <div style={{ display: "flex", gap: "10px", marginTop: "20px", fontSize: "0.85rem", color: C.muted, alignItems: "center", paddingLeft: "88px" }}>
                  <span>Less</span>
                  <div style={{ width: "18px", height: "18px", backgroundColor: C.empty, borderRadius: "3px" }} />
                  <div style={{ width: "18px", height: "18px", backgroundColor: C.r0, borderRadius: "3px" }} />
                  <div style={{ width: "18px", height: "18px", backgroundColor: C.r1, borderRadius: "3px" }} />
                  <div style={{ width: "18px", height: "18px", backgroundColor: C.r2, borderRadius: "3px" }} />
                  <div style={{ width: "18px", height: "18px", backgroundColor: C.r3, borderRadius: "3px" }} />
                  <div style={{ width: "18px", height: "18px", backgroundColor: C.r4, borderRadius: "3px" }} />
                  <div style={{ width: "18px", height: "18px", backgroundColor: C.r5, borderRadius: "3px" }} />
                  <span>More</span>
                </div>
              </div>
              <p style={{ fontSize: "0.8rem", color: C.muted, marginTop: "15px", fontStyle: "italic" }}>
                💡 Hover over squares to see details. Color intensity shows reading duration per hour.
              </p>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    );
  };

  const renderCalendarHeatmap = (title, data, dataKey, thresholds) => {
    if (!data || data.length === 0) return null;

    // Create a map of dates to values
    const dateMap = new Map();
    data.forEach((item) => {
      dateMap.set(item.date, item[dataKey]);
    });

    // Generate all dates for the past year
    const endDate = new Date();
    const startDate = new Date(Math.max(
      new Date().setFullYear(new Date().getFullYear() - 1),
      new Date(2025, 9, 1)
    ));
    
    // Adjust startDate to the previous Sunday (or keep it if it's already Sunday)
    const adjustedStartDate = new Date(startDate);
    const dayOfWeek = adjustedStartDate.getDay();
    if (dayOfWeek !== 0) {
      adjustedStartDate.setDate(adjustedStartDate.getDate() - dayOfWeek);
    }
    
    const weeks = [];
    let currentDate = new Date(adjustedStartDate);
    
    while (currentDate <= endDate) {
      const week = [];
      
      // Always create a full week starting from Sunday
      for (let i = 0; i < 7; i++) {
        const dateStr = currentDate.toISOString().split('T')[0];
        const value = dateMap.get(dateStr) || 0;
        week.push({ date: dateStr, value, dayOfWeek: currentDate.getDay() });
        currentDate.setDate(currentDate.getDate() + 1);
      }
      
      weeks.push(week);
      if (currentDate > endDate) break;
    }

    const getColor = (value) => {
      if (value === 0) return C.empty;
      const sortedThresholds = Object.entries(thresholds).sort((a, b) => parseFloat(a[0]) - parseFloat(b[0]));
      for (let i = sortedThresholds.length - 1; i >= 0; i--) {
        if (value >= parseFloat(sortedThresholds[i][0])) {
          return sortedThresholds[i][1];
        }
      }
      return thresholds[0];
    };

    // Calculate square size to fit width
    const numWeeks = weeks.length;
    const gapSize = 4;
    const totalGaps = (numWeeks - 1) * gapSize;
    const squareSize = `calc((100% - ${totalGaps}px) / ${numWeeks})`;

    // Get month labels
    const monthLabels = [];
    let lastMonth = '';
    weeks.forEach((week, index) => {
      const firstDay = week[0];
      const date = new Date(firstDay.date);
      const monthName = date.toLocaleDateString('en-US', { month: 'short' });
      if (monthName !== lastMonth) {
        monthLabels.push({ index, label: monthName });
        lastMonth = monthName;
      }
    });

    return (
      <Row className="mt-4 mb-4">
        <Col md={12}>
          <Card className="chart-card">
            <Card.Body>
              <Card.Title>{title}</Card.Title>
              <div style={{ padding: "20px 0" }}>
                <div style={{ width: "100%" }}>
                  {/* Month labels */}
                  <div style={{ display: "flex", gap: `${gapSize}px`, width: "100%", marginBottom: "8px", paddingLeft: "30px" }}>
                    {weeks.map((week, weekIndex) => {
                      const monthLabel = monthLabels.find(m => m.index === weekIndex);
                      return (
                        <div key={weekIndex} style={{ flex: 1, fontSize: "0.75rem", color: C.muted, fontWeight: "bold" }}>
                          {monthLabel ? monthLabel.label : ''}
                        </div>
                      );
                    })}
                  </div>
                  
                  <div style={{ display: "flex", gap: `${gapSize}px` }}>
                    {/* Day of week labels */}
                    <div style={{ display: "flex", flexDirection: "column", gap: `${gapSize}px`, justifyContent: "space-around", paddingRight: "8px" }}>
                      {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, idx) => (
                        <div key={idx} style={{ fontSize: "0.7rem", color: C.muted, height: "18px", display: "flex", alignItems: "center" }}>
                          {idx % 2 === 1 ? day : ''}
                        </div>
                      ))}
                    </div>
                    
                    {/* Heatmap grid */}
                    <div style={{ display: "flex", gap: `${gapSize}px`, flex: 1 }}>
                      {weeks.map((week, weekIndex) => (
                        <div key={weekIndex} style={{ display: "flex", flexDirection: "column", gap: `${gapSize}px`, flex: 1 }}>
                          {week.map((day, dayIndex) => (
                            <div
                              key={dayIndex}
                              title={`${day.date}: ${day.value} ${dataKey}`}
                              style={{
                                width: "100%",
                                paddingBottom: "100%",
                                position: "relative",
                                backgroundColor: getColor(day.value),
                                borderRadius: "3px",
                                cursor: "pointer",
                                transition: "all 0.2s",
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.transform = "scale(1.2)";
                                e.currentTarget.style.zIndex = "10";
                                e.currentTarget.style.boxShadow = C.glow;
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.transform = "scale(1)";
                                e.currentTarget.style.zIndex = "1";
                                e.currentTarget.style.boxShadow = "none";
                              }}
                            />
                          ))}
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <div style={{ display: "flex", gap: "10px", marginTop: "20px", fontSize: "0.85rem", color: C.muted, alignItems: "center", paddingLeft: "30px" }}>
                    <span>Less</span>
                    {Object.entries(thresholds).sort((a, b) => parseFloat(a[0]) - parseFloat(b[0])).slice(1, 6).map(([threshold, color], idx) => (
                      <div key={idx} style={{ width: "18px", height: "18px", backgroundColor: color, borderRadius: "3px" }} />
                    ))}
                    <span>More</span>
                  </div>
                </div>
              </div>
              <p style={{ fontSize: "0.8rem", color: C.muted, marginTop: "15px", fontStyle: "italic" }}>
                💡 Hover over squares to see details. Each square represents one day.
              </p>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    );
  };

  // Chart navigation items
  const chartNavItems = [
    { id: "summary", title: "📊 Summary" },
    { id: "insights", title: "🧠 Insights" },
    { id: "sessionCap", title: "⏱️ Session Cap" },
    { id: "bursts", title: "💥 Bursts" },
    { id: "revisited", title: "🔄 Revisited" },
    { id: "dominance", title: "👑 Dominance" },
    { id: "dataQuality", title: "🔧 Quality" },
    { id: "paceOverTime", title: "📉 Pace Trends" },
    { id: "engagementScatter", title: "🎨 Eng vs Pace" },
    { id: "timeHeatmap", title: "🌡️ Time Heat" },
    { id: "engagement", title: "🎯 Engagement" },
    { id: "circadian", title: "⏰ Circadian" },
    { id: "momentum", title: "📈 Momentum" },
    { id: "clusters", title: "🔬 Clusters" },
    { id: "temporal", title: "🕐 Habits" },
    { id: "authorStyle", title: "✒️ Styles" },
    { id: "anomalies", title: "🔍 Anomalies" },
    { id: "heatmapHours", title: "🔥 Hours" },
    { id: "heatmapPages", title: "📄 Ref. Pages" },
    { id: "weekly", title: "📈 Weekly" },
    { id: "authors", title: "✍️ Authors" },
    { id: "dayOfWeek", title: "📅 By Day" },
    { id: "bubble", title: "💭 Patterns" },
    { id: "longest", title: "📚 Longest" },
    { id: "timeInvested", title: "⏱️ Time" },
    { id: "streaks", title: "🔥 Streaks" },
    { id: "speed", title: "🚀 Speed" },
    { id: "yearly", title: "📆 Yearly" },
    { id: "allBooks", title: "📖 All Books" },
  ];

  // Render selected chart
  const renderSelectedChart = () => {
    switch (selectedChart) {
      case "summary":
        return (
          <>
            {/* 2-Week Timeline at the top */}
            {renderTwoWeekTimeline()}
            
            <Row>
              <Col md={3}>
                <Card className="stat-card">
                  <Card.Body>
                    <Card.Title>Total Books</Card.Title>
                    <Card.Text className="stat-number">
                      {stats.totalBooks}
                    </Card.Text>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card className="stat-card">
                  <Card.Body>
                    <Card.Title>Reading Time</Card.Title>
                    <Card.Text className="stat-number">
                      {stats.totalReadingTimeHours}h
                    </Card.Text>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card className="stat-card">
                  <Card.Body>
                    <Card.Title>Ref. Pages Read</Card.Title>
                    <Card.Text
                      className="stat-number"
                      style={{ fontSize: "2rem" }}
                    >
                      {stats.totalPagesRead?.toLocaleString()}
                    </Card.Text>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card className="stat-card">
                  <Card.Body>
                    <Card.Title>Avg Speed</Card.Title>
                    <Card.Text className="stat-number">
                      {stats.avgSpeed}{" "}
                      <span style={{ fontSize: "1rem" }}>ref. pg/h</span>
                    </Card.Text>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            <div className="mt-4">
              <BookProgress books={stats.progressBooks} progress={stats.progressRows} />
            </div>

            {/* Currently Reading Section */}
            <Row className="mt-4">
              <Col md={12}>
                <Card className="chart-card">
                  <Card.Body>
                    <Card.Title>📖 Currently Reading</Card.Title>
                    <Card.Text style={{ fontSize: "0.9rem", color: C.muted, marginBottom: "15px" }}>
                      Books with activity in the last 7 days
                    </Card.Text>
                    <div style={{ padding: "10px 0" }}>
                      {stats.currentlyReading?.length > 0 ? (
                        stats.currentlyReading.map((book, index) => {
                          const completionPercent = completionPct(book);
                          const timeHours = Math.round((book.timeSeconds / 3600) * 10) / 10;
                          const daysAgo = Math.floor((Date.now() / 1000 - book.lastOpen) / 86400);
                          
                          return (
                            <div
                              key={index}
                              style={{
                                padding: "15px",
                                marginBottom: "12px",
                                backgroundColor: "rgba(76, 175, 80, 0.15)",
                                borderRadius: "8px",
                                borderLeft: "4px solid #4CAF50",
                              }}
                            >
                              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", marginBottom: "8px" }}>
                                <div style={{ flex: 1 }}>
                                  <div style={{ fontWeight: "bold", fontSize: "1.1rem", color: C.text, marginBottom: "3px" }}>
                                    {book.title}
                                  </div>
                                  <div style={{ fontSize: "0.85rem", color: C.muted, marginBottom: "5px" }}>
                                    {book.author}
                                  </div>
                                </div>
                                <div style={{ textAlign: "right", minWidth: "80px" }}>
                                  <div style={{ fontSize: "1.2rem", fontWeight: "bold", color: C.green }}>
                                    {completionPercent}%
                                  </div>
                                  <div style={{ fontSize: "0.7rem", color: C.muted }}>
                                    complete
                                  </div>
                                </div>
                              </div>
                              <div style={{ fontSize: "0.9rem", color: C.text, marginBottom: "8px" }}>
                                📄 Ref. page {book.currentPage} of {book.totalPages} • 
                                ⏱️ {timeHours}h total • 
                                📅 {book.daysActiveLastWeek} days active this week
                              </div>
                              <div style={{ fontSize: "0.8rem", color: C.dim }}>
                                Last read: {daysAgo === 0 ? 'Today' : daysAgo === 1 ? 'Yesterday' : `${daysAgo} days ago`}
                              </div>
                              <div style={{ marginTop: "8px" }}>
                                <div style={{ width: "100%", height: "6px", backgroundColor: C.grid, borderRadius: "3px", overflow: "hidden" }}>
                                  <div style={{ width: `${completionPercent}%`, height: "100%", backgroundColor: C.green, transition: "width 0.3s" }}></div>
                                </div>
                              </div>
                            </div>
                          );
                        })
                      ) : (
                        <div style={{ padding: "20px", textAlign: "center", color: C.muted }}>
                          No recent reading activity in the last 7 days
                        </div>
                      )}
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Last 2 Weeks Activity */}
            <Row className="mt-4">
              <Col md={12}>
                <Card className="chart-card">
                  <Card.Body>
                    <Card.Title>📊 Last 2 Weeks Activity</Card.Title>
                    <Row className="mt-3">
                      <Col md={2}>
                        <div style={{ textAlign: "center", padding: "15px", backgroundColor: "rgba(33, 150, 243, 0.15)", borderRadius: "8px" }}>
                          <div style={{ fontSize: "2rem", fontWeight: "bold", color: C.blue }}>
                            {stats.twoWeeksActivity?.daysRead || 0}
                          </div>
                          <div style={{ fontSize: "0.85rem", color: C.muted }}>Days Read</div>
                        </div>
                      </Col>
                      <Col md={2}>
                        <div style={{ textAlign: "center", padding: "15px", backgroundColor: "rgba(156, 39, 176, 0.15)", borderRadius: "8px" }}>
                          <div style={{ fontSize: "2rem", fontWeight: "bold", color: C.purple }}>
                            {stats.twoWeeksActivity?.totalHours || 0}h
                          </div>
                          <div style={{ fontSize: "0.85rem", color: C.muted }}>Total Hours</div>
                        </div>
                      </Col>
                      <Col md={2}>
                        <div style={{ textAlign: "center", padding: "15px", backgroundColor: "rgba(255, 152, 0, 0.15)", borderRadius: "8px" }}>
                          <div style={{ fontSize: "2rem", fontWeight: "bold", color: C.orange }}>
                            {stats.twoWeeksActivity?.totalSessions || 0}
                          </div>
                          <div style={{ fontSize: "0.85rem", color: C.muted }}>Sessions</div>
                        </div>
                      </Col>
                      <Col md={3}>
                        <div style={{ textAlign: "center", padding: "15px", backgroundColor: "rgba(76, 175, 80, 0.15)", borderRadius: "8px" }}>
                          <div style={{ fontSize: "2rem", fontWeight: "bold", color: C.green }}>
                            {stats.twoWeeksActivity?.booksTouched || 0}
                          </div>
                          <div style={{ fontSize: "0.85rem", color: C.muted }}>Books Touched</div>
                        </div>
                      </Col>
                      <Col md={3}>
                        <div style={{ textAlign: "center", padding: "15px", backgroundColor: "rgba(255, 193, 7, 0.15)", borderRadius: "8px" }}>
                          <div style={{ fontSize: "2rem", fontWeight: "bold", color: C.amber }}>
                            {stats.twoWeeksActivity?.avgSessionDuration || 0}s
                          </div>
                          <div style={{ fontSize: "0.85rem", color: C.muted }}>Avg Session</div>
                        </div>
                      </Col>
                    </Row>
                    
                    {/* Additional Metrics Row */}
                    <Row className="mt-4">
                      <Col md={4}>
                        <div style={{ textAlign: "center", padding: "15px", backgroundColor: "rgba(244, 67, 54, 0.15)", borderRadius: "8px" }}>
                          <div style={{ fontSize: "1.8rem", fontWeight: "bold", color: C.red }}>
                            {stats.twoWeeksActivity?.totalPages || 0}
                          </div>
                          <div style={{ fontSize: "0.85rem", color: C.muted }}>Ref. Pages Read</div>
                        </div>
                      </Col>
                      <Col md={4}>
                        <div style={{ textAlign: "center", padding: "15px", backgroundColor: "rgba(103, 58, 183, 0.15)", borderRadius: "8px" }}>
                          <div style={{ fontSize: "1.8rem", fontWeight: "bold", color: C.indigo }}>
                            {stats.twoWeeksActivity?.totalHours > 0 
                              ? Math.round((stats.twoWeeksActivity.totalPages / stats.twoWeeksActivity.totalHours) * 10) / 10
                              : 0}
                          </div>
                          <div style={{ fontSize: "0.85rem", color: C.muted }}>Pages/Hour</div>
                        </div>
                      </Col>
                      <Col md={4}>
                        <div style={{ textAlign: "center", padding: "15px", backgroundColor: "rgba(233, 30, 99, 0.15)", borderRadius: "8px" }}>
                          <div style={{ fontSize: "1.8rem", fontWeight: "bold", color: C.pink }}>
                            {stats.twoWeeksActivity?.daysRead > 0 
                              ? Math.round((stats.twoWeeksActivity.totalHours / stats.twoWeeksActivity.daysRead) * 10) / 10
                              : 0}h
                          </div>
                          <div style={{ fontSize: "0.85rem", color: C.muted }}>Avg Hours/Day</div>
                        </div>
                      </Col>
                    </Row>

                    {/* Week Comparison */}
                    {stats.weekComparison && (
                      <div style={{ marginTop: "25px", padding: "15px", backgroundColor: "rgba(33, 150, 243, 0.1)", borderRadius: "8px", borderLeft: "4px solid #2196F3" }}>
                        <h6 style={{ color: C.blue, marginBottom: "15px" }}>📈 Week-over-Week Comparison</h6>
                        <Row>
                          <Col md={6}>
                            <div style={{ marginBottom: "15px" }}>
                              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "5px" }}>
                                <span style={{ color: C.text, fontSize: "0.9rem" }}>This Week</span>
                                <span style={{ color: C.text, fontWeight: "bold" }}>
                                  {stats.weekComparison.week2Hours}h • {stats.weekComparison.week2Pages} ref. pages
                                </span>
                              </div>
                              <div style={{ width: "100%", height: "8px", backgroundColor: C.grid, borderRadius: "4px", overflow: "hidden" }}>
                                <div style={{ 
                                  width: `${Math.min((stats.weekComparison.week2Hours / Math.max(stats.weekComparison.week1Hours, stats.weekComparison.week2Hours, 1)) * 100, 100)}%`, 
                                  height: "100%", 
                                  backgroundColor: C.green, 
                                  transition: "width 0.3s" 
                                }}></div>
                              </div>
                            </div>
                          </Col>
                          <Col md={6}>
                            <div style={{ marginBottom: "15px" }}>
                              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "5px" }}>
                                <span style={{ color: C.text, fontSize: "0.9rem" }}>Last Week</span>
                                <span style={{ color: C.text, fontWeight: "bold" }}>
                                  {stats.weekComparison.week1Hours}h • {stats.weekComparison.week1Pages} ref. pages
                                </span>
                              </div>
                              <div style={{ width: "100%", height: "8px", backgroundColor: C.grid, borderRadius: "4px", overflow: "hidden" }}>
                                <div style={{ 
                                  width: `${Math.min((stats.weekComparison.week1Hours / Math.max(stats.weekComparison.week1Hours, stats.weekComparison.week2Hours, 1)) * 100, 100)}%`, 
                                  height: "100%", 
                                  backgroundColor: C.orange, 
                                  transition: "width 0.3s" 
                                }}></div>
                              </div>
                            </div>
                          </Col>
                        </Row>
                        <div style={{ fontSize: "0.85rem", color: C.muted, marginTop: "10px", textAlign: "center" }}>
                          {stats.weekComparison.week2Hours > stats.weekComparison.week1Hours 
                            ? `📈 Up ${Math.round(((stats.weekComparison.week2Hours - stats.weekComparison.week1Hours) / stats.weekComparison.week1Hours) * 100)}% from last week`
                            : stats.weekComparison.week2Hours < stats.weekComparison.week1Hours
                            ? `📉 Down ${Math.round(((stats.weekComparison.week1Hours - stats.weekComparison.week2Hours) / stats.weekComparison.week1Hours) * 100)}% from last week`
                            : '➡️ Same as last week'}
                        </div>
                      </div>
                    )}

                    {/* Peak Hours */}
                    {stats.twoWeeksPeakHours && stats.twoWeeksPeakHours.length > 0 && (
                      <div style={{ marginTop: "25px", padding: "15px", backgroundColor: "rgba(255, 152, 0, 0.1)", borderRadius: "8px", borderLeft: "4px solid #FF9800" }}>
                        <h6 style={{ color: C.orange, marginBottom: "15px" }}>⏰ Peak Reading Hours</h6>
                        <div style={{ display: "flex", gap: "10px", justifyContent: "space-around" }}>
                          {stats.twoWeeksPeakHours.map((peak, index) => {
                            const hour = peak.hour;
                            const displayHour = hour === 0 ? "12 AM" : hour < 12 ? `${hour} AM` : hour === 12 ? "12 PM" : `${hour - 12} PM`;
                            return (
                              <div key={index} style={{ textAlign: "center", flex: 1 }}>
                                <div style={{ fontSize: "1.5rem", fontWeight: "bold", color: C.orange }}>
                                  {displayHour}
                                </div>
                                <div style={{ fontSize: "0.8rem", color: C.muted }}>
                                  {peak.sessions} sessions • {peak.hours}h
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Most Engaged Book */}
                    {stats.twoWeeksMostEngaged && (
                      <div style={{ marginTop: "25px", padding: "15px", backgroundColor: "rgba(156, 39, 176, 0.1)", borderRadius: "8px", borderLeft: "4px solid #9C27B0" }}>
                        <h6 style={{ color: C.purple, marginBottom: "10px" }}>🎯 Most Engaged Book</h6>
                        <div style={{ fontWeight: "bold", fontSize: "1.1rem", color: C.text, marginBottom: "5px" }}>
                          {stats.twoWeeksMostEngaged.title}
                        </div>
                        <div style={{ fontSize: "0.9rem", color: C.muted, marginBottom: "8px" }}>
                          {stats.twoWeeksMostEngaged.author}
                        </div>
                        <div style={{ fontSize: "0.9rem", color: C.text }}>
                          💡 {stats.twoWeeksMostEngaged.highlights} highlights • 
                          📝 {stats.twoWeeksMostEngaged.notes} notes
                        </div>
                      </div>
                    )}

                    {/* Longest Session */}
                    {stats.twoWeeksLongestSession && (
                      <div style={{ marginTop: "25px", padding: "15px", backgroundColor: "rgba(76, 175, 80, 0.1)", borderRadius: "8px", borderLeft: "4px solid #4CAF50" }}>
                        <h6 style={{ color: C.green, marginBottom: "10px" }}>💪 Longest Reading Session</h6>
                        <div style={{ fontWeight: "bold", fontSize: "1.1rem", color: C.text, marginBottom: "5px" }}>
                          {stats.twoWeeksLongestSession.title}
                        </div>
                        <div style={{ fontSize: "0.9rem", color: C.muted, marginBottom: "5px" }}>
                          {new Date(stats.twoWeeksLongestSession.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        </div>
                        <div style={{ fontSize: "1.3rem", fontWeight: "bold", color: C.green }}>
                          {Math.round(stats.twoWeeksLongestSession.duration / 60)} minutes
                        </div>
                      </div>
                    )}

                    {/* Daily Activity Chart */}
                    {stats.twoWeeksDaily && stats.twoWeeksDaily.length > 0 && (
                      <div style={{ marginTop: "25px" }}>
                        <h6 style={{ color: C.text, marginBottom: "15px" }}>📊 Daily Reading Activity</h6>
                        <div style={{ display: "flex", gap: "6px", alignItems: "flex-end", height: "140px", padding: "10px", backgroundColor: C.panel2, borderRadius: "8px" }}>
                          {stats.twoWeeksDaily.map((day, index) => {
                            const maxHours = Math.max(...stats.twoWeeksDaily.map(d => d.hours), 1);
                            const maxSessions = Math.max(...stats.twoWeeksDaily.map(d => d.sessions), 1);
                            const hoursHeightPercent = (day.hours / maxHours) * 100;
                            const sessionsHeightPercent = (day.sessions / maxSessions) * 100;
                            const date = new Date(day.date);
                            const dayLabel = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                            const minutes = Math.round(day.hours * 60);
                            
                            return (
                              <div key={index} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", height: "100%" }}>
                                <div style={{ flex: 1, display: "flex", alignItems: "flex-end", width: "100%", gap: "2px" }}>
                                  {/* Minutes bar */}
                                  <div
                                    title={`${dayLabel}\n${minutes} minutes\n${day.sessions} ref. pages`}
                                    style={{
                                      width: "48%",
                                      height: `${hoursHeightPercent}%`,
                                      backgroundColor: day.hours > 0 ? C.green : C.empty,
                                      borderRadius: "4px 4px 0 0",
                                      cursor: "pointer",
                                      transition: "all 0.2s",
                                      position: "relative",
                                    }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.backgroundColor = day.hours > 0 ? C.greenHi : C.empty;
                                      e.currentTarget.style.transform = "scaleY(1.05)";
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.backgroundColor = day.hours > 0 ? C.green : C.empty;
                                      e.currentTarget.style.transform = "scaleY(1)";
                                    }}
                                  />
                                  {/* Pages bar */}
                                  <div
                                    title={`${dayLabel}\n${minutes} minutes\n${day.sessions} ref. pages`}
                                    style={{
                                      width: "48%",
                                      height: `${sessionsHeightPercent}%`,
                                      backgroundColor: day.sessions > 0 ? C.blue : C.empty,
                                      borderRadius: "4px 4px 0 0",
                                      cursor: "pointer",
                                      transition: "all 0.2s",
                                      position: "relative",
                                    }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.backgroundColor = day.sessions > 0 ? C.blueHi : C.empty;
                                      e.currentTarget.style.transform = "scaleY(1.05)";
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.backgroundColor = day.sessions > 0 ? C.blue : C.empty;
                                      e.currentTarget.style.transform = "scaleY(1)";
                                    }}
                                  />
                                </div>
                                <div style={{ fontSize: "0.65rem", color: C.dim, marginTop: "5px", textAlign: "center" }}>
                                  {date.getDate()}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        <div style={{ display: "flex", gap: "15px", justifyContent: "center", marginTop: "15px", fontSize: "0.85rem" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                            <div style={{ width: "12px", height: "12px", backgroundColor: C.green, borderRadius: "2px" }}></div>
                            <span style={{ color: C.muted }}>Minutes</span>
                          </div>
                          <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                            <div style={{ width: "12px", height: "12px", backgroundColor: C.blue, borderRadius: "2px" }}></div>
                            <span style={{ color: C.muted }}>Pages</span>
                          </div>
                        </div>
                        <div style={{ fontSize: "0.8rem", color: C.muted, marginTop: "5px", textAlign: "center" }}>
                          Hover over bars to see details
                        </div>
                      </div>
                    )}

                    <div style={{ marginTop: "25px" }}>
                      <h6 style={{ color: C.text, marginBottom: "15px" }}>📚 Top Books (Last 2 Weeks)</h6>
                      {stats.twoWeeksBooks?.length > 0 ? (
                        stats.twoWeeksBooks.map((book, index) => (
                          <div
                            key={index}
                            style={{
                              padding: "12px",
                              marginBottom: "10px",
                              backgroundColor: "rgba(63, 81, 181, 0.15)",
                              borderRadius: "8px",
                              borderLeft: "4px solid rgba(63, 81, 181, 1)",
                            }}
                          >
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                              <div style={{ flex: 1 }}>
                                <div style={{ fontWeight: "bold", fontSize: "1rem", color: C.text, marginBottom: "3px" }}>
                                  {book.title}
                                </div>
                                <div style={{ fontSize: "0.85rem", color: C.muted }}>
                                  {book.author}
                                </div>
                              </div>
                              <div style={{ textAlign: "right", minWidth: "150px" }}>
                                <div style={{ fontSize: "0.9rem", color: C.text }}>
                                  {book.hours}h • {book.sessions} sessions
                                </div>
                                <div style={{ fontSize: "0.8rem", color: C.muted }}>
                                  {book.daysActive} days active
                                </div>
                              </div>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div style={{ padding: "20px", textAlign: "center", color: C.muted }}>
                          No reading activity in the last 2 weeks
                        </div>
                      )}
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </>
        );

      case "insights":
        return (
          <Row>
            <Col md={12}>
              <Card className="chart-card" style={{ marginBottom: "20px" }}>
                <Card.Body>
                  <Card.Title>🧠 Key Reading Insights</Card.Title>
                  <div style={{ padding: "15px 0" }}>
                    <div style={{ marginBottom: "25px", padding: "15px", backgroundColor: "rgba(76, 175, 80, 0.1)", borderRadius: "8px", borderLeft: "4px solid #4CAF50" }}>
                      <h5 style={{ color: C.green, marginBottom: "10px" }}>📊 Reading Consistency</h5>
                      <p style={{ color: C.text, fontSize: "1.1rem", marginBottom: "5px" }}>
                        <strong>{stats.consistency?.consistencyScore}%</strong> consistency score
                      </p>
                      <p style={{ color: C.muted, fontSize: "0.9rem" }}>
                        You read on {stats.consistency?.totalDays} days with an average of {stats.consistency?.avgSessions} sessions per day ({stats.consistency?.avgMinutes} minutes). 
                        Your most productive day had {stats.consistency?.maxSessions} sessions!
                      </p>
                    </div>
                    
                    <div style={{ marginBottom: "25px", padding: "15px", backgroundColor: "rgba(33, 150, 243, 0.1)", borderRadius: "8px", borderLeft: "4px solid #2196F3" }}>
                      <h5 style={{ color: C.blue, marginBottom: "10px" }}>🎯 Engagement Pattern</h5>
                      <p style={{ color: C.text, fontSize: "1.1rem", marginBottom: "5px" }}>
                        Average reading speed: <strong>{stats.avgSpeed} ref. pages/hour</strong>
                      </p>
                      <p style={{ color: C.muted, fontSize: "0.9rem" }}>
                        Books that survive past 20 sessions have a 90% completion rate. Your abandonment typically happens within the first 6 sessions, suggesting you know quickly if a book hooks you.
                      </p>
                    </div>

                    <div style={{ marginBottom: "25px", padding: "15px", backgroundColor: "rgba(255, 152, 0, 0.1)", borderRadius: "8px", borderLeft: "4px solid #FF9800" }}>
                      <h5 style={{ color: C.orange, marginBottom: "10px" }}>⏰ Optimal Reading Windows</h5>
                      <p style={{ color: C.text, fontSize: "1.1rem", marginBottom: "5px" }}>
                        Peak focus: <strong>11 AM - 2 PM</strong>
                      </p>
                      <p style={{ color: C.muted, fontSize: "0.9rem" }}>
                        While you read most by volume in early morning (4-5 AM), your deepest engagement happens mid-day with 20-30% longer sessions.
                      </p>
                    </div>

                    <div style={{ padding: "15px", backgroundColor: "rgba(156, 39, 176, 0.1)", borderRadius: "8px", borderLeft: "4px solid #9C27B0" }}>
                      <h5 style={{ color: C.purple, marginBottom: "10px" }}>📈 Reading Momentum</h5>
                      <p style={{ color: C.text, fontSize: "1.1rem", marginBottom: "5px" }}>
                        Optimal session spacing: <strong>30-90 minutes</strong>
                      </p>
                      <p style={{ color: C.muted, fontSize: "0.9rem" }}>
                        You read most effectively when sessions are spaced 30-90 minutes apart, suggesting you prefer "binge reading" in concentrated bursts rather than daily habits.
                      </p>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        );

      case "paceOverTime":
        return (
          <Row>
            <Col md={12}>
              <Card className="chart-card">
                <Card.Body>
                  <Card.Title>📉 Reading Pace Over Time</Card.Title>
                  <Card.Text style={{ fontSize: "0.9rem", color: C.muted, marginBottom: "15px" }}>
                    How your reading pace (seconds per reference page) changes over weeks
                  </Card.Text>
                  <div className="chart-container">
                    <Line data={paceOverTimeData} options={weeklyTrendOptions} />
                  </div>
                  <Card.Text style={{ fontSize: "0.85rem", color: C.muted, marginTop: "15px", fontStyle: "italic" }}>
                    💡 Insight: Pace variations reveal reading fatigue, flow states, or "sprint to finish" patterns. Slower pace may indicate denser content or deeper engagement.
                  </Card.Text>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        );

      case "engagementScatter":
        return (
          <Row>
            <Col md={12}>
              <Card className="chart-card">
                <Card.Body>
                  <Card.Title>🎨 Engagement vs Reading Pace</Card.Title>
                  <Card.Text style={{ fontSize: "0.9rem", color: C.muted, marginBottom: "15px" }}>
                    Scatter plot showing relationship between reading pace and engagement (notes+highlights). Bubble size = time invested.
                  </Card.Text>
                  <div className="chart-container">
                    <Bubble data={engagementScatterData} options={engagementScatterOptions} />
                  </div>
                  <Card.Text style={{ fontSize: "0.85rem", color: C.muted, marginTop: "15px", fontStyle: "italic" }}>
                    💡 Insight: Books in the upper-right quadrant are "deep thinking" books (slow pace, high engagement), while lower-left are "page-turners" (fast pace, low engagement).
                  </Card.Text>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        );

      case "timeHeatmap":
        return (
          <>
            {/* Session Density Heatmap */}
            <Row>
              <Col md={12}>
                <Card className="chart-card">
                  <Card.Body>
                    <Card.Title>🌡️ Time-of-Day Reading Heatmap (Session Density)</Card.Title>
                    <Card.Text style={{ fontSize: "0.9rem", color: C.muted, marginBottom: "15px" }}>
                      When do you actually read? Session density by hour and day of week.
                    </Card.Text>
                    <div style={{ padding: "20px 0", overflowX: "auto" }}>
                      <div style={{ minWidth: "800px" }}>
                        {["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"].map((day, dayIndex) => (
                          <div key={day} style={{ display: "flex", alignItems: "center", marginBottom: "8px" }}>
                            <div style={{ width: "100px", fontSize: "0.9rem", color: C.text, fontWeight: "bold" }}>
                              {day}
                            </div>
                            <div style={{ display: "flex", gap: "2px", flex: 1 }}>
                              {Array.from({ length: 24 }, (_, hour) => {
                                const cellData = stats.timeOfDayHeatmap?.find(
                                  (d) => d.weekday === dayIndex && d.hour === hour
                                );
                                const sessions = cellData?.sessions || 0;
                                const intensity = sessions > 0 ? Math.min(sessions / 50, 1) : 0;
                                const color = sessions === 0 ? C.empty : 
                                  intensity > 0.7 ? C.r5 :
                                  intensity > 0.5 ? C.r4 :
                                  intensity > 0.3 ? C.r3 :
                                  intensity > 0.1 ? C.r2 : C.r0;
                                
                                return (
                                  <div
                                    key={hour}
                                    title={`${day} ${hour}:00 - ${sessions} sessions`}
                                    style={{
                                      flex: 1,
                                      height: "30px",
                                      backgroundColor: color,
                                      borderRadius: "2px",
                                      cursor: "pointer",
                                      transition: "all 0.2s",
                                    }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.transform = "scale(1.1)";
                                      e.currentTarget.style.zIndex = "10";
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.transform = "scale(1)";
                                      e.currentTarget.style.zIndex = "1";
                                    }}
                                  />
                                );
                              })}
                            </div>
                          </div>
                        ))}
                        <div style={{ display: "flex", justifyContent: "space-between", marginTop: "15px", fontSize: "0.75rem", color: C.muted }}>
                          <span>12 AM</span>
                          <span>6 AM</span>
                          <span>12 PM</span>
                          <span>6 PM</span>
                          <span>11 PM</span>
                        </div>
                      </div>
                    </div>
                    <Card.Text style={{ fontSize: "0.85rem", color: C.muted, marginTop: "15px", fontStyle: "italic" }}>
                      💡 Insight: Darker colors indicate higher session density. This reveals your true reading patterns across the week.
                    </Card.Text>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Time Duration Heatmap */}
            <Row className="mt-4">
              <Col md={12}>
                <Card className="chart-card">
                  <Card.Body>
                    <Card.Title>⏱️ Time-of-Day Reading Duration Heatmap</Card.Title>
                    <Card.Text style={{ fontSize: "0.9rem", color: C.muted, marginBottom: "15px" }}>
                      Total hours spent reading during each hour of the day across all days of the week.
                    </Card.Text>
                    <div style={{ padding: "20px 0", overflowX: "auto" }}>
                      <div style={{ minWidth: "800px" }}>
                        {/* Calculate max hours for proportional coloring */}
                        {(() => {
                          const maxHours = Math.max(
                            ...Array.from({ length: 7 }, (_, dayIndex) =>
                              Array.from({ length: 24 }, (_, hour) => {
                                const cellData = stats.timeOfDayHeatmap?.find(
                                  (d) => d.weekday === dayIndex && d.hour === hour
                                );
                                return cellData?.totalHours || 0;
                              })
                            ).flat()
                          );

                          return (
                            <>
                              {["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"].map((day, dayIndex) => (
                                <div key={day} style={{ display: "flex", alignItems: "center", marginBottom: "8px" }}>
                                  <div style={{ width: "100px", fontSize: "0.9rem", color: C.text, fontWeight: "bold" }}>
                                    {day}
                                  </div>
                                  <div style={{ display: "flex", gap: "2px", flex: 1 }}>
                                    {Array.from({ length: 24 }, (_, hour) => {
                                      const cellData = stats.timeOfDayHeatmap?.find(
                                        (d) => d.weekday === dayIndex && d.hour === hour
                                      );
                                      const hours = cellData?.totalHours || 0;
                                      const proportion = maxHours > 0 ? hours / maxHours : 0;
                                      
                                      // Color scale based on proportion
                                      const getColor = (prop) => {
                                        if (prop === 0) return C.empty;
                                        if (prop >= 0.8) return C.r5;  // Darkest red
                                        if (prop >= 0.6) return C.r4;  // Tomato
                                        if (prop >= 0.4) return C.r3;  // Hot pink
                                        if (prop >= 0.2) return C.r2;  // Medium orchid
                                        if (prop >= 0.1) return C.r1;  // Medium purple
                                        return C.r0;                    // Cornflower blue
                                      };
                                      
                                      const color = getColor(proportion);
                                      const displayHour = hour === 0 ? "12 AM" : hour < 12 ? `${hour} AM` : hour === 12 ? "12 PM" : `${hour - 12} PM`;
                                      const minutes = Math.round(hours * 60);
                                      
                                      return (
                                        <div
                                          key={hour}
                                          title={`${day} ${displayHour}\n${hours.toFixed(2)} hours (${minutes} min)\n${cellData?.sessions || 0} sessions`}
                                          style={{
                                            flex: 1,
                                            height: "30px",
                                            backgroundColor: color,
                                            borderRadius: "2px",
                                            cursor: "pointer",
                                            transition: "all 0.2s",
                                            position: "relative",
                                          }}
                                          onMouseEnter={(e) => {
                                            e.currentTarget.style.transform = "scale(1.1)";
                                            e.currentTarget.style.zIndex = "10";
                                            e.currentTarget.style.boxShadow = C.glow;
                                          }}
                                          onMouseLeave={(e) => {
                                            e.currentTarget.style.transform = "scale(1)";
                                            e.currentTarget.style.zIndex = "1";
                                            e.currentTarget.style.boxShadow = "none";
                                          }}
                                        />
                                      );
                                    })}
                                  </div>
                                </div>
                              ))}
                              <div style={{ display: "flex", justifyContent: "space-between", marginTop: "15px", fontSize: "0.75rem", color: C.muted }}>
                                <span>12 AM</span>
                                <span>6 AM</span>
                                <span>12 PM</span>
                                <span>6 PM</span>
                                <span>11 PM</span>
                              </div>
                              
                              {/* Color Legend */}
                              <div style={{ display: "flex", gap: "10px", marginTop: "20px", fontSize: "0.85rem", color: C.muted, alignItems: "center", justifyContent: "center" }}>
                                <span>Less</span>
                                <div style={{ width: "18px", height: "18px", backgroundColor: C.empty, borderRadius: "3px" }} />
                                <div style={{ width: "18px", height: "18px", backgroundColor: C.r0, borderRadius: "3px" }} />
                                <div style={{ width: "18px", height: "18px", backgroundColor: C.r1, borderRadius: "3px" }} />
                                <div style={{ width: "18px", height: "18px", backgroundColor: C.r2, borderRadius: "3px" }} />
                                <div style={{ width: "18px", height: "18px", backgroundColor: C.r3, borderRadius: "3px" }} />
                                <div style={{ width: "18px", height: "18px", backgroundColor: C.r4, borderRadius: "3px" }} />
                                <div style={{ width: "18px", height: "18px", backgroundColor: C.r5, borderRadius: "3px" }} />
                                <span>More</span>
                              </div>
                              
                              {/* Stats Summary */}
                              <div style={{ marginTop: "20px", padding: "15px", backgroundColor: "rgba(33, 150, 243, 0.1)", borderRadius: "8px", borderLeft: "4px solid #2196F3" }}>
                                <h6 style={{ color: C.blue, marginBottom: "10px" }}>📊 Reading Time Summary</h6>
                                <div style={{ fontSize: "0.9rem", color: C.text }}>
                                  <div>Peak reading hour: <strong>{(() => {
                                    let maxH = 0;
                                    let maxHourVal = 0;
                                    for (let h = 0; h < 24; h++) {
                                      const total = Array.from({ length: 7 }, (_, d) => {
                                        const cell = stats.timeOfDayHeatmap?.find(
                                          (item) => item.weekday === d && item.hour === h
                                        );
                                        return cell?.totalHours || 0;
                                      }).reduce((a, b) => a + b, 0);
                                      if (total > maxHourVal) {
                                        maxHourVal = total;
                                        maxH = h;
                                      }
                                    }
                                    const displayH = maxH === 0 ? "12 AM" : maxH < 12 ? `${maxH} AM` : maxH === 12 ? "12 PM" : `${maxH - 12} PM`;
                                    return `${displayH} (${maxHourVal.toFixed(2)} hours total)`;
                                  })()}</strong></div>
                                  <div style={{ marginTop: "5px" }}>Most active day: <strong>{(() => {
                                    const dayTotals = Array.from({ length: 7 }, (_, d) => {
                                      const total = Array.from({ length: 24 }, (_, h) => {
                                        const cell = stats.timeOfDayHeatmap?.find(
                                          (item) => item.weekday === d && item.hour === h
                                        );
                                        return cell?.totalHours || 0;
                                      }).reduce((a, b) => a + b, 0);
                                      return { day: ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"][d], total };
                                    });
                                    const maxDay = dayTotals.reduce((max, curr) => curr.total > max.total ? curr : max);
                                    return `${maxDay.day} (${maxDay.total.toFixed(2)} hours)`;
                                  })()}</strong></div>
                                </div>
                              </div>
                            </>
                          );
                        })()}
                      </div>
                    </div>
                    <Card.Text style={{ fontSize: "0.85rem", color: C.muted, marginTop: "15px", fontStyle: "italic" }}>
                      💡 Insight: Color intensity shows proportional reading time. Darker colors indicate hours where you spend the most time reading across all days.
                    </Card.Text>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </>
        );

      case "engagement":
        return (
          <>
            <Row>
              <Col md={12}>
                <Card className="chart-card">
                  <Card.Body>
                    <Card.Title>🎯 Reading Engagement Patterns</Card.Title>
                    <Card.Text style={{ fontSize: "0.9rem", color: C.muted, marginBottom: "15px" }}>
                      Books with highest average session duration (minutes)
                    </Card.Text>
                    <div style={{ padding: "10px 0" }}>
                      {stats.engagementPatterns?.map((book, index) => (
                        <div key={index} style={{ padding: "12px", marginBottom: "10px", backgroundColor: "rgba(63, 81, 181, 0.2)", borderRadius: "8px", borderLeft: "4px solid rgba(63, 81, 181, 1)" }}>
                          <div style={{ fontWeight: "bold", fontSize: "1rem", marginBottom: "5px", color: C.text }}>
                            {book.title}
                          </div>
                          <div style={{ fontSize: "0.9rem", color: C.muted }}>
                            {book.sessions} sessions • {book.avgSessionMin} min/session • {book.totalHours}h total
                          </div>
                        </div>
                      ))}
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
            <Row className="mt-4">
              <Col md={12}>
                <Card className="chart-card">
                  <Card.Body>
                    <Card.Title>📊 Session Duration Distribution</Card.Title>
                    <div style={{ padding: "10px 0" }}>
                      {stats.sessionClustering?.map((cluster, index) => (
                        <div key={index} style={{ marginBottom: "15px" }}>
                          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "5px" }}>
                            <span style={{ color: C.text, fontWeight: "bold" }}>{cluster.type}</span>
                            <span style={{ color: C.muted }}>{cluster.count} sessions ({cluster.percentage}%)</span>
                          </div>
                          <div style={{ width: "100%", height: "8px", backgroundColor: C.grid, borderRadius: "4px", overflow: "hidden" }}>
                            <div style={{ width: `${cluster.percentage}%`, height: "100%", backgroundColor: C.green, transition: "width 0.3s" }}></div>
                          </div>
                        </div>
                      ))}
                    </div>
                    <Card.Text style={{ fontSize: "0.85rem", color: C.muted, marginTop: "15px", fontStyle: "italic" }}>
                      💡 Insight: Your bimodal distribution (34% long sessions, 31% quick sessions) suggests decisive engagement - you either commit deeply or quickly scan.
                    </Card.Text>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </>
        );

      case "circadian":
        return (
          <Row>
            <Col md={12}>
              <Card className="chart-card">
                <Card.Body>
                  <Card.Title>⏰ Circadian Reading Patterns</Card.Title>
                  <Card.Text style={{ fontSize: "0.9rem", color: C.muted, marginBottom: "15px" }}>
                    Peak performance hours (by average session duration)
                  </Card.Text>
                  <div style={{ padding: "10px 0" }}>
                    {stats.circadianPatterns?.map((pattern, index) => {
                      const hour = parseInt(pattern.hour);
                      const displayHour = hour === 0 ? "12 AM" : hour < 12 ? `${hour} AM` : hour === 12 ? "12 PM" : `${hour - 12} PM`;
                      const colors = C.series10;
                      const color = colors[index % colors.length];
                      
                      return (
                        <div key={index} style={{ padding: "12px", marginBottom: "10px", backgroundColor: `${color}20`, borderRadius: "8px", borderLeft: `4px solid ${color}` }}>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <div>
                              <div style={{ fontWeight: "bold", fontSize: "1.1rem", color: C.text }}>
                                {displayHour}
                              </div>
                              <div style={{ fontSize: "0.85rem", color: C.muted }}>
                                {pattern.sessions} sessions • {pattern.hours}h total
                              </div>
                            </div>
                            <div style={{ textAlign: "right" }}>
                              <div style={{ fontSize: "1.3rem", fontWeight: "bold", color }}>
                                {pattern.avgDuration}s
                              </div>
                              <div style={{ fontSize: "0.75rem", color: C.muted }}>
                                avg duration
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <Card.Text style={{ fontSize: "0.85rem", color: C.muted, marginTop: "15px", fontStyle: "italic" }}>
                    💡 Insight: You're a morning reader by volume, but your deepest engagement happens mid-day (11 AM-2 PM) with 20-30% longer sessions.
                  </Card.Text>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        );

      case "momentum":
        return (
          <Row>
            <Col md={12}>
              <Card className="chart-card">
                <Card.Body>
                  <Card.Title>📈 Reading Momentum & Streaks</Card.Title>
                  <Card.Text style={{ fontSize: "0.9rem", color: C.muted, marginBottom: "15px" }}>
                    Books with sustained reading momentum (days active)
                  </Card.Text>
                  <div style={{ padding: "10px 0" }}>
                    {stats.readingMomentum?.map((book, index) => {
                      const intensity = book.daysActive / book.spanDays;
                      const intensityColor = intensity > 0.7 ? C.green : intensity > 0.4 ? C.orange : C.red;
                      
                      return (
                        <div key={index} style={{ padding: "12px", marginBottom: "10px", backgroundColor: "rgba(255, 193, 7, 0.1)", borderRadius: "8px", borderLeft: `4px solid ${intensityColor}` }}>
                          <div style={{ fontWeight: "bold", fontSize: "1rem", marginBottom: "5px", color: C.text }}>
                            {book.title}
                          </div>
                          <div style={{ fontSize: "0.9rem", color: C.muted, marginBottom: "8px" }}>
                            {book.daysActive} days active over {book.spanDays} day span
                          </div>
                          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                            <div style={{ flex: 1, height: "6px", backgroundColor: C.grid, borderRadius: "3px", overflow: "hidden" }}>
                              <div style={{ width: `${(intensity * 100).toFixed(0)}%`, height: "100%", backgroundColor: intensityColor, transition: "width 0.3s" }}></div>
                            </div>
                            <span style={{ fontSize: "0.85rem", color: intensityColor, fontWeight: "bold" }}>
                              {(intensity * 100).toFixed(0)}% intensity
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <Card.Text style={{ fontSize: "0.85rem", color: C.muted, marginTop: "15px", fontStyle: "italic" }}>
                    💡 Insight: Books with 0.6-1.5 hour gaps between sessions correlate with completion, suggesting you prefer concentrated reading bursts.
                  </Card.Text>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        );

      case "clusters":
        return (
          <Row>
            <Col md={12}>
              <Card className="chart-card">
                <Card.Body>
                  <Card.Title>🔬 Session Clustering Analysis</Card.Title>
                  <Card.Text style={{ fontSize: "0.9rem", color: C.muted, marginBottom: "15px" }}>
                    Reading patterns by time of day - Deep reads (&gt;500s) vs quick scans
                  </Card.Text>
                  <div style={{ padding: "10px 0" }}>
                    {stats.sessionClustersAdv?.map((cluster, index) => {
                      const colors = { Morning: C.amber, Afternoon: C.green, Evening: C.purple, Night: C.blue };
                      const color = colors[cluster.timeBucket] || C.fallback;
                      
                      return (
                        <div key={index} style={{ padding: "15px", marginBottom: "15px", backgroundColor: `${color}15`, borderRadius: "8px", borderLeft: `4px solid ${color}` }}>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
                            <div>
                              <div style={{ fontWeight: "bold", fontSize: "1.2rem", color: C.text }}>
                                {cluster.timeBucket}
                              </div>
                              <div style={{ fontSize: "0.85rem", color: C.muted }}>
                                {cluster.sessions} total sessions
                              </div>
                            </div>
                            <div style={{ textAlign: "right" }}>
                              <div style={{ fontSize: "1.3rem", fontWeight: "bold", color }}>
                                {cluster.avgDuration}s
                              </div>
                              <div style={{ fontSize: "0.75rem", color: C.muted }}>
                                avg duration
                              </div>
                            </div>
                          </div>
                          <div style={{ marginTop: "10px" }}>
                            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "5px" }}>
                              <span style={{ fontSize: "0.9rem", color: C.text }}>Deep Read Sessions (&gt;500s)</span>
                              <span style={{ fontSize: "0.9rem", color, fontWeight: "bold" }}>
                                {cluster.deepSessions} ({cluster.deepPct}%)
                              </span>
                            </div>
                            <div style={{ width: "100%", height: "6px", backgroundColor: C.grid, borderRadius: "3px", overflow: "hidden" }}>
                              <div style={{ width: `${cluster.deepPct}%`, height: "100%", backgroundColor: color, transition: "width 0.3s" }}></div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <Card.Text style={{ fontSize: "0.85rem", color: C.muted, marginTop: "15px", fontStyle: "italic" }}>
                    💡 Insight: Three distinct clusters emerge - quick scans (mornings), deep reads (afternoons/evenings), and skims. Deep reads dominate after initial exploration phases.
                  </Card.Text>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        );

      case "temporal":
        return (
          <Row>
            <Col md={12}>
              <Card className="chart-card">
                <Card.Body>
                  <Card.Title>🕐 Temporal Habit Loops</Card.Title>
                  <Card.Text style={{ fontSize: "0.9rem", color: C.muted, marginBottom: "15px" }}>
                    Your reading habits by day of week - when do you typically read?
                  </Card.Text>
                  <div style={{ padding: "10px 0" }}>
                    {stats.temporalHabits?.map((habit, index) => {
                      const hour = habit.avgHour;
                      const displayTime = hour === 0 ? "12 AM" : hour < 12 ? `${Math.floor(hour)} AM` : hour === 12 ? "12 PM" : `${Math.floor(hour - 12)} PM`;
                      const dayColors = [C.red, C.pink, C.purple, C.indigo, "#3F51B5", C.blue, C.green];
                      const color = dayColors[index % dayColors.length];
                      
                      return (
                        <div key={index} style={{ padding: "12px", marginBottom: "10px", backgroundColor: `${color}15`, borderRadius: "8px", borderLeft: `4px solid ${color}` }}>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <div>
                              <div style={{ fontWeight: "bold", fontSize: "1.1rem", color: C.text }}>
                                {habit.day}
                              </div>
                              <div style={{ fontSize: "0.85rem", color: C.muted }}>
                                {habit.sessions} sessions
                              </div>
                            </div>
                            <div style={{ textAlign: "right" }}>
                              <div style={{ fontSize: "1.2rem", fontWeight: "bold", color }}>
                                ~{displayTime}
                              </div>
                              <div style={{ fontSize: "0.75rem", color: C.muted }}>
                                typical time
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <Card.Text style={{ fontSize: "0.85rem", color: C.muted, marginTop: "15px", fontStyle: "italic" }}>
                    💡 Insight: 24h autocorrelation shows consistent daily cycles. Your reading habits form predictable loops, with 78% of sessions occurring within ±4h of your typical daily pattern.
                  </Card.Text>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        );

      case "authorStyle":
        return (
          <Row>
            <Col md={12}>
              <Card className="chart-card">
                <Card.Body>
                  <Card.Title>✒️ Author Style Fingerprints</Card.Title>
                  <Card.Text style={{ fontSize: "0.9rem", color: C.muted, marginBottom: "15px" }}>
                    Authors ranked by relative reading pace (how fast you read them vs. your average)
                  </Card.Text>
                  <div style={{ padding: "10px 0" }}>
                    {stats.authorStyles?.map((author, index) => {
                      const isFast = author.relativePace > 1.2;
                      const isSlow = author.relativePace < 0.8;
                      const color = isFast ? C.green : isSlow ? C.deepOrange : C.amber;
                      const label = isFast ? "Fast-paced" : isSlow ? "Contemplative" : "Moderate";
                      
                      return (
                        <div key={index} style={{ padding: "12px", marginBottom: "10px", backgroundColor: `${color}15`, borderRadius: "8px", borderLeft: `4px solid ${color}` }}>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: "bold", fontSize: "1rem", color: C.text, marginBottom: "3px" }}>
                                {author.author}
                              </div>
                              <div style={{ fontSize: "0.85rem", color: C.muted }}>
                                {author.sessions} sessions • {author.meanDuration}s avg
                              </div>
                            </div>
                            <div style={{ textAlign: "right" }}>
                              <div style={{ fontSize: "1.3rem", fontWeight: "bold", color }}>
                                {author.relativePace}x
                              </div>
                              <div style={{ fontSize: "0.75rem", color, fontWeight: "bold" }}>
                                {label}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <Card.Text style={{ fontSize: "0.85rem", color: C.muted, marginTop: "15px", fontStyle: "italic" }}>
                    💡 Insight: Author entropy reveals writing styles - uniform session variance indicates consistent pacing, while high variance suggests puzzle-like or complex content requiring varied attention.
                  </Card.Text>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        );

      case "anomalies":
        return (
          <Row>
            <Col md={12}>
              <Card className="chart-card">
                <Card.Body>
                  <Card.Title>🔍 Completion Anomaly Detection</Card.Title>
                  <Card.Text style={{ fontSize: "0.9rem", color: C.muted, marginBottom: "15px" }}>
                    Books with unusual reading patterns (statistical outliers)
                  </Card.Text>
                  <div style={{ padding: "10px 0" }}>
                    {stats.completionAnomalies?.map((anomaly, index) => {
                      const patternColors = {
                        "Fast Completion": C.green,
                        "Slow Abandonment": C.red,
                        "Normal": C.amber
                      };
                      const color = patternColors[anomaly.pattern] || C.fallback;
                      
                      return (
                        <div key={index} style={{ padding: "12px", marginBottom: "10px", backgroundColor: `${color}15`, borderRadius: "8px", borderLeft: `4px solid ${color}` }}>
                          <div style={{ marginBottom: "8px" }}>
                            <div style={{ fontWeight: "bold", fontSize: "1rem", color: C.text, marginBottom: "3px" }}>
                              {anomaly.title}
                            </div>
                            <div style={{ display: "inline-block", padding: "2px 8px", backgroundColor: color, borderRadius: "12px", fontSize: "0.75rem", fontWeight: "bold", color: "white" }}>
                              {anomaly.pattern}
                            </div>
                          </div>
                          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "10px", fontSize: "0.85rem", color: C.muted }}>
                            <div>
                              <span style={{ color: C.dim }}>Completion:</span>{" "}
                              <span style={{ color: C.text, fontWeight: "bold" }}>{anomaly.compPct}%</span>
                            </div>
                            <div>
                              <span style={{ color: C.dim }}>Sec/Ref. Page:</span>{" "}
                              <span style={{ color: C.text, fontWeight: "bold" }}>{anomaly.secPerPage}s</span>
                            </div>
                            <div>
                              <span style={{ color: C.dim }}>Anomaly:</span>{" "}
                              <span style={{ color, fontWeight: "bold" }}>
                                {anomaly.timeAnomaly > 0 ? "+" : ""}{anomaly.timeAnomaly}s
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <Card.Text style={{ fontSize: "0.85rem", color: C.muted, marginTop: "15px", fontStyle: "italic" }}>
                    💡 Insight: Outliers reveal "abandoned mid-deep-dive" patterns - books with high completion but unusually fast pace may indicate skimming, while slow abandonment suggests difficulty or disinterest.
                  </Card.Text>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        );

      case "sessionCap":
        return (
          <Row>
            <Col md={12}>
              <Card className="chart-card">
                <Card.Body>
                  <Card.Title>⏱️ Session Duration Cap Analysis</Card.Title>
                  <Card.Text style={{ fontSize: "0.9rem", color: C.muted, marginBottom: "15px" }}>
                    Top session durations - 36% of sessions hit the 120s timeout cap
                  </Card.Text>
                  <div style={{ padding: "10px 0" }}>
                    {stats.sessionDurationCap?.map((item, index) => {
                      const isAtCap = item.duration === 120;
                      const color = isAtCap ? C.red : C.green;
                      const pct = ((item.count / stats.dataQuality?.totalSessions) * 100).toFixed(1);
                      
                      return (
                        <div key={index} style={{ marginBottom: "15px" }}>
                          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "5px" }}>
                            <span style={{ color: C.text, fontWeight: "bold" }}>
                              {item.duration}s {isAtCap && "⚠️ (TIMEOUT CAP)"}
                            </span>
                            <span style={{ color: C.muted }}>{item.count} sessions ({pct}%)</span>
                          </div>
                          <div style={{ width: "100%", height: "10px", backgroundColor: C.grid, borderRadius: "4px", overflow: "hidden" }}>
                            <div style={{ width: `${pct}%`, height: "100%", backgroundColor: color, transition: "width 0.3s" }}></div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <Card.Text style={{ fontSize: "0.85rem", color: C.muted, marginTop: "15px", fontStyle: "italic" }}>
                    💡 Insight: 594 sessions (36%) are exactly 120s, suggesting a hard timeout cap rather than natural variation. This indicates your reading app may have a 2-minute session limit.
                  </Card.Text>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        );

      case "bursts":
        return (
          <Row>
            <Col md={12}>
              <Card className="chart-card">
                <Card.Body>
                  <Card.Title>💥 Reading Burst Detection</Card.Title>
                  <Card.Text style={{ fontSize: "0.9rem", color: C.muted, marginBottom: "15px" }}>
                    Days with intense reading activity (30+ sessions)
                  </Card.Text>
                  <div style={{ padding: "10px 0" }}>
                    {stats.readingBursts?.map((burst, index) => {
                      const colors = C.series;
                      const color = colors[index % colors.length];
                      
                      return (
                        <div key={index} style={{ padding: "15px", marginBottom: "12px", backgroundColor: `${color}20`, borderRadius: "8px", borderLeft: `4px solid ${color}` }}>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: "bold", fontSize: "1.1rem", color: C.text, marginBottom: "5px" }}>
                                {burst.date}
                              </div>
                              <div style={{ fontSize: "0.9rem", color: C.muted }}>
                                {burst.title}
                              </div>
                            </div>
                            <div style={{ textAlign: "right" }}>
                              <div style={{ fontSize: "1.5rem", fontWeight: "bold", color }}>
                                {burst.sessions}
                              </div>
                              <div style={{ fontSize: "0.75rem", color: C.muted }}>
                                sessions • {burst.hours}h
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <Card.Text style={{ fontSize: "0.85rem", color: C.muted, marginTop: "15px", fontStyle: "italic" }}>
                    💡 Insight: Two major bursts detected - Nov 23 (112 sessions on Strange Pictures) and Dec 6 (51 sessions on Intercepts). You engage in intense, focused reading sessions rather than steady daily reading.
                  </Card.Text>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        );

      case "revisited":
        return (
          <Row>
            <Col md={12}>
              <Card className="chart-card">
                <Card.Body>
                  <Card.Title>🔄 Most Revisited Pages</Card.Title>
                  <Card.Text style={{ fontSize: "0.9rem", color: C.muted, marginBottom: "15px" }}>
                    Reference pages with the most total reading time (indicating difficulty or importance)
                  </Card.Text>
                  <div style={{ padding: "10px 0" }}>
                    {stats.topRevisitedPages?.map((page, index) => (
                      <div key={index} style={{ padding: "12px", marginBottom: "10px", backgroundColor: "rgba(255, 193, 7, 0.15)", borderRadius: "8px", borderLeft: "4px solid #FFC107" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: "bold", fontSize: "1rem", color: C.text, marginBottom: "3px" }}>
                              {page.title} - Ref. page {page.page}
                            </div>
                            <div style={{ fontSize: "0.85rem", color: C.muted }}>
                              {page.visitCount} visits
                            </div>
                          </div>
                          <div style={{ textAlign: "right" }}>
                            <div style={{ fontSize: "1.2rem", fontWeight: "bold", color: C.amber }}>
                              {page.totalMinutes} min
                            </div>
                            <div style={{ fontSize: "0.75rem", color: C.muted }}>
                              total time
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <Card.Text style={{ fontSize: "0.85rem", color: C.muted, marginTop: "15px", fontStyle: "italic" }}>
                    💡 Insight: Page 23 of Intercepts has 40+ minutes total time - the most revisited page. High revisit time suggests either difficulty requiring re-reading or particularly important/interesting content.
                  </Card.Text>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        );

      case "dominance":
        return (
          <Row>
            <Col md={12}>
              <Card className="chart-card">
                <Card.Body>
                  <Card.Title>👑 Book Dominance Analysis</Card.Title>
                  <Card.Text style={{ fontSize: "0.9rem", color: C.muted, marginBottom: "15px" }}>
                    Session distribution across books - 3 books account for 95% of activity
                  </Card.Text>
                  <div style={{ padding: "10px 0" }}>
                    {stats.bookDominance?.map((book, index) => {
                      const isTopThree = index < 3;
                      const color = isTopThree ? C.green : C.fallback;
                      
                      return (
                        <div key={index} style={{ padding: "12px", marginBottom: "10px", backgroundColor: isTopThree ? "rgba(76, 175, 80, 0.15)" : "rgba(100, 100, 100, 0.1)", borderRadius: "8px", borderLeft: `4px solid ${color}` }}>
                          <div style={{ marginBottom: "8px" }}>
                            <div style={{ fontWeight: "bold", fontSize: "1rem", color: C.text, marginBottom: "3px" }}>
                              {book.title}
                            </div>
                            <div style={{ fontSize: "0.85rem", color: C.muted }}>
                              {book.author} • {book.sessions} sessions • {book.hours}h • {book.pagesRead} ref. pages
                            </div>
                          </div>
                          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                            <div style={{ flex: 1, height: "8px", backgroundColor: C.grid, borderRadius: "4px", overflow: "hidden" }}>
                              <div style={{ width: `${book.sessionPct}%`, height: "100%", backgroundColor: color, transition: "width 0.3s" }}></div>
                            </div>
                            <span style={{ fontSize: "0.9rem", color, fontWeight: "bold", minWidth: "50px", textAlign: "right" }}>
                              {book.sessionPct}%
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <Card.Text style={{ fontSize: "0.85rem", color: C.muted, marginTop: "15px", fontStyle: "italic" }}>
                    💡 Insight: Stark divide - 3 "highly-read" books (Strange Pictures, Hidden Pictures, Housemaid) dominate with 28,000-35,000s each, while 4 others have under 18,000s. This shows focused, intensive reading rather than broad sampling.
                  </Card.Text>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        );

      case "dataQuality":
        return (
          <Row>
            <Col md={12}>
              <Card className="chart-card">
                <Card.Body>
                  <Card.Title>🔧 Data Quality Report</Card.Title>
                  <Card.Text style={{ fontSize: "0.9rem", color: C.muted, marginBottom: "15px" }}>
                    Database consistency and anomaly metrics
                  </Card.Text>
                  <Row>
                    <Col md={6}>
                      <div style={{ padding: "15px", marginBottom: "15px", backgroundColor: "rgba(244, 67, 54, 0.1)", borderRadius: "8px", borderLeft: "4px solid #F44336" }}>
                        <div style={{ fontSize: "2rem", fontWeight: "bold", color: C.red, marginBottom: "5px" }}>
                          {stats.dataQuality?.sessionsAtCap || 0}
                        </div>
                        <div style={{ fontSize: "0.9rem", color: C.text, marginBottom: "3px" }}>
                          Sessions at 120s Cap
                        </div>
                        <div style={{ fontSize: "0.8rem", color: C.muted }}>
                          {((stats.dataQuality?.sessionsAtCap / stats.dataQuality?.totalSessions) * 100).toFixed(1)}% of total sessions
                        </div>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div style={{ padding: "15px", marginBottom: "15px", backgroundColor: "rgba(255, 152, 0, 0.1)", borderRadius: "8px", borderLeft: "4px solid #FF9800" }}>
                        <div style={{ fontSize: "2rem", fontWeight: "bold", color: C.orange, marginBottom: "5px" }}>
                          {stats.dataQuality?.pagesExceeding || 0}
                        </div>
                        <div style={{ fontSize: "0.9rem", color: C.text, marginBottom: "3px" }}>
                          Pages Beyond Stored Book Length
                        </div>
                        <div style={{ fontSize: "0.8rem", color: C.muted }}>
                          Page numbers beyond the stored book length (different screens or editions)
                        </div>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div style={{ padding: "15px", marginBottom: "15px", backgroundColor: "rgba(33, 150, 243, 0.1)", borderRadius: "8px", borderLeft: "4px solid #2196F3" }}>
                        <div style={{ fontSize: "2rem", fontWeight: "bold", color: C.blue, marginBottom: "5px" }}>
                          {stats.dataQuality?.pageCountMismatches || 0}
                        </div>
                        <div style={{ fontSize: "0.9rem", color: C.text, marginBottom: "3px" }}>
                          Page Count Mismatches
                        </div>
                        <div style={{ fontSize: "0.8rem", color: C.muted }}>
                          Sessions whose layout differs from the stored page count (rebased in the stats)
                        </div>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div style={{ padding: "15px", marginBottom: "15px", backgroundColor: "rgba(76, 175, 80, 0.1)", borderRadius: "8px", borderLeft: "4px solid #4CAF50" }}>
                        <div style={{ fontSize: "2rem", fontWeight: "bold", color: C.green, marginBottom: "5px" }}>
                          {stats.dataQuality?.totalSessions || 0}
                        </div>
                        <div style={{ fontSize: "0.9rem", color: C.text, marginBottom: "3px" }}>
                          Total Sessions
                        </div>
                        <div style={{ fontSize: "0.8rem", color: C.muted }}>
                          Across all books
                        </div>
                      </div>
                    </Col>
                  </Row>
                  <Card.Text style={{ fontSize: "0.85rem", color: C.muted, marginTop: "15px", fontStyle: "italic" }}>
                    💡 Insight: Data shows timeout caps (120s limit), edition inconsistencies (e.g., Pines sessions at page 351 but only 297 pages listed), and varying page counts across sessions. These suggest app limitations and format differences.
                  </Card.Text>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        );

      case "heatmapHours":
        return renderCalendarHeatmap("Hours Read", stats.calendarData, "hours", {
          0: C.empty,
          0.3: C.r0,
          0.6: C.r1,
          1: C.r2,
          1.5: C.r6,
          2: C.r3,
          3: C.r4,
          4: C.r5,
        });

      case "heatmapPages":
        return renderCalendarHeatmap("Ref. Pages Read", stats.calendarPagesData, "pages", {
          0: C.empty,
          10: C.r0,
          25: C.r1,
          50: C.r2,
          75: C.r6,
          100: C.r3,
          150: C.r4,
          200: C.r5,
        });

      case "weekly":
        return (
          <Row>
            <Col md={12}>
              <Card className="chart-card">
                <Card.Body>
                  <Card.Title>Weekly Reading Trend</Card.Title>
                  <div className="chart-container">
                    <Line data={weeklyTrendData} options={weeklyTrendOptions} />
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        );

      case "authors":
        return (
          <Row>
            <Col md={12}>
              <Card className="chart-card">
                <Card.Body>
                  <Card.Title>Top 10 Authors</Card.Title>
                  <div className="chart-container">
                    <Bar data={topAuthorsData} options={chartOptions} />
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        );

      case "dayOfWeek":
        return (
          <Row>
            <Col md={12}>
              <Card className="chart-card">
                <Card.Body>
                  <Card.Title>Reading Activity by Day of Week</Card.Title>
                  <div className="chart-container">
                    <Bar data={readingByDayData} options={chartOptions} />
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        );

      case "bubble":
        return stats.bubbleChartData && stats.bubbleChartData.length > 0 ? (
          <Row>
            <Col md={12}>
              <Card className="chart-card">
                <Card.Body>
                  <Card.Title>
                    Reading Patterns: Sessions vs Time vs Pages
                  </Card.Title>
                  <Card.Text
                    style={{
                      fontSize: "0.9rem",
                      color: C.muted,
                      marginBottom: "15px",
                    }}
                  >
                    Bubble size represents book length. Hover over bubbles for
                    details.
                  </Card.Text>
                  <div className="chart-container">
                    <Bubble data={bubbleData} options={bubbleOptions} />
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        ) : null;

      case "longest":
        return (
          <Row>
            <Col md={12}>
              <Card className="chart-card">
                <Card.Body>
                  <Card.Title>Longest Books Read (by reference pages)</Card.Title>
                  <div style={{ padding: "10px 0" }}>
                    {stats.longestBooks?.map((book, index) => (
                      <div
                        key={index}
                        style={{
                          padding: "12px",
                          marginBottom: "10px",
                          backgroundColor: "rgba(75, 192, 192, 0.2)",
                          borderRadius: "8px",
                          borderLeft: "4px solid rgba(75, 192, 192, 1)",
                        }}
                      >
                        <div
                          style={{
                            fontWeight: "bold",
                            fontSize: "1rem",
                            marginBottom: "5px",
                            color: C.text,
                          }}
                        >
                          {book.title}
                        </div>
                        <div style={{ fontSize: "0.9rem", color: C.muted }}>
                          {book.author} • {book.pagesRead} of {book.totalPages} ref. pages read
                          {book.totalPages > 0 && ` (${completionPct(book)}% complete)`}
                        </div>
                      </div>
                    ))}
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        );

      case "timeInvested":
        return (
          <Row>
            <Col md={12}>
              <Card className="chart-card">
                <Card.Body>
                  <Card.Title>Most Time Invested</Card.Title>
                  <div style={{ padding: "10px 0" }}>
                    {stats.mostTimeBooks?.map((book, index) => (
                      <div
                        key={index}
                        style={{
                          padding: "12px",
                          marginBottom: "10px",
                          backgroundColor: "rgba(153, 102, 255, 0.2)",
                          borderRadius: "8px",
                          borderLeft: "4px solid rgba(153, 102, 255, 1)",
                        }}
                      >
                        <div
                          style={{
                            fontWeight: "bold",
                            fontSize: "1rem",
                            marginBottom: "5px",
                            color: C.text,
                          }}
                        >
                          {book.title}
                        </div>
                        <div style={{ fontSize: "0.9rem", color: C.muted }}>
                          {book.author} • {book.timeHours} hours • {book.pages}{" "}
                          ref. pages read
                        </div>
                      </div>
                    ))}
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        );

      case "streaks":
        return (
          <>
            <Row>
              <Col md={6}>
                <Card className="stat-card">
                  <Card.Body>
                    <Card.Title>Current Streak 🔥</Card.Title>
                    <Card.Text className="stat-number">
                      {stats.currentStreak || 0}
                    </Card.Text>
                    <Card.Text
                      style={{
                        fontSize: "0.9rem",
                        color: C.muted,
                        marginTop: "10px",
                      }}
                    >
                      Consecutive days reading
                    </Card.Text>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={6}>
                <Card className="stat-card">
                  <Card.Body>
                    <Card.Title>Longest Streak 🏆</Card.Title>
                    <Card.Text className="stat-number">
                      {stats.longestStreak || 0}
                    </Card.Text>
                    <Card.Text
                      style={{
                        fontSize: "0.9rem",
                        color: C.muted,
                        marginTop: "10px",
                      }}
                    >
                      Best reading streak
                    </Card.Text>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
            <Row className="mt-4">
              <Col md={12}>
                <Card className="stat-card">
                  <Card.Body>
                    <Card.Title>
                      Reading Momentum{" "}
                      {stats.weekChange > 10
                        ? "🔥"
                        : stats.weekChange < -10
                        ? "❄️"
                        : "➡️"}
                    </Card.Title>
                    <Card.Text className="stat-number">
                      {stats.lastWeekTime || 0}h
                    </Card.Text>
                    <Card.Text
                      style={{
                        fontSize: "0.9rem",
                        color: C.muted,
                        marginTop: "10px",
                      }}
                    >
                      Last 7 days • {stats.weekChange > 0 ? "+" : ""}
                      {stats.weekChange}% vs. previous week
                    </Card.Text>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </>
        );

      case "speed":
        return (
          <>
            <Row>
              <Col md={12}>
                <Card className="chart-card">
                  <Card.Body>
                    <Card.Title>Fastest Books 🚀</Card.Title>
                    <Card.Text
                      style={{
                        fontSize: "0.9rem",
                        color: C.muted,
                        marginBottom: "15px",
                      }}
                    >
                      Books you read at the highest speed
                    </Card.Text>
                    <div style={{ padding: "10px 0" }}>
                      {stats.fastestBooks?.map((book, index) => (
                        <div
                          key={index}
                          style={{
                            padding: "12px",
                            marginBottom: "10px",
                            backgroundColor: "rgba(76, 175, 80, 0.2)",
                            borderRadius: "8px",
                            borderLeft: "4px solid rgba(76, 175, 80, 1)",
                          }}
                        >
                          <div
                            style={{
                              fontWeight: "bold",
                              fontSize: "1rem",
                              marginBottom: "5px",
                              color: C.text,
                            }}
                          >
                            {book.title}
                          </div>
                          <div style={{ fontSize: "0.9rem", color: C.muted }}>
                            {book.author} • {book.ppm} ref. pages/min
                          </div>
                        </div>
                      ))}
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
            <Row className="mt-4">
              <Col md={12}>
                <Card className="chart-card">
                  <Card.Body>
                    <Card.Title>Most Challenging Books 🎯</Card.Title>
                    <Card.Text
                      style={{
                        fontSize: "0.9rem",
                        color: C.muted,
                        marginBottom: "15px",
                      }}
                    >
                      Books that required more focus and time
                    </Card.Text>
                    <div style={{ padding: "10px 0" }}>
                      {stats.slowestBooks?.map((book, index) => (
                        <div
                          key={index}
                          style={{
                            padding: "12px",
                            marginBottom: "10px",
                            backgroundColor: "rgba(255, 152, 0, 0.2)",
                            borderRadius: "8px",
                            borderLeft: "4px solid rgba(255, 152, 0, 1)",
                          }}
                        >
                          <div
                            style={{
                              fontWeight: "bold",
                              fontSize: "1rem",
                              marginBottom: "5px",
                              color: C.text,
                            }}
                          >
                            {book.title}
                          </div>
                          <div style={{ fontSize: "0.9rem", color: C.muted }}>
                            {book.author} • {book.ppm} ref. pages/min
                          </div>
                        </div>
                      ))}
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </>
        );

      case "yearly":
        return (
          <Row>
            <Col md={12}>
              <Card className="chart-card">
                <Card.Body>
                  <Card.Title>Books Read Per Year</Card.Title>
                  <div className="chart-container">
                    <Bar data={booksPerYearData} options={chartOptions} />
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        );

      case "allBooks":
        return (
          <>
            <Row>
              <Col md={12}>
                <Card className="chart-card">
                  <Card.Body>
                    <Card.Title>All Books 📚</Card.Title>
                    <Card.Text
                      style={{
                        fontSize: "0.9rem",
                        color: C.muted,
                        marginBottom: "15px",
                      }}
                    >
                      Click on any book to see detailed insights
                    </Card.Text>
                    <div
                      style={{
                        padding: "10px 0",
                        maxHeight: "500px",
                        overflowY: "auto",
                      }}
                    >
                      {stats.allBooks?.map((book, index) => {
                        const completionPercent = completionPct(book);
                        const timeHours =
                          Math.round((book.timeSeconds / 3600) * 10) / 10;
                        const ppm =
                          book.timeSeconds > 0
                            ? Math.round(
                                (book.pagesRead / (book.timeSeconds / 60)) * 10
                              ) / 10
                            : 0;

                        return (
                          <div
                            key={index}
                            onClick={() => setSelectedBook(book)}
                            style={{
                              padding: "12px",
                              marginBottom: "10px",
                              backgroundColor:
                                selectedBook?.id === book.id
                                  ? "rgba(63, 81, 181, 0.3)"
                                  : "rgba(63, 81, 181, 0.15)",
                              borderRadius: "8px",
                              borderLeft: "4px solid rgba(63, 81, 181, 1)",
                              cursor: "pointer",
                              transition: "all 0.2s",
                            }}
                            onMouseEnter={(e) =>
                              (e.currentTarget.style.backgroundColor =
                                "rgba(63, 81, 181, 0.3)")
                            }
                            onMouseLeave={(e) => {
                              if (selectedBook?.id !== book.id) {
                                e.currentTarget.style.backgroundColor =
                                  "rgba(63, 81, 181, 0.15)";
                              }
                            }}
                          >
                            <div
                              style={{
                                fontWeight: "bold",
                                fontSize: "1rem",
                                marginBottom: "5px",
                                color: C.text,
                              }}
                            >
                              {book.title}
                            </div>
                            <div style={{ fontSize: "0.9rem", color: C.muted }}>
                              {book.author} • {book.pagesRead}/{book.totalPages}{" "}
                              ref. pages ({completionPercent}%) • {timeHours}h •{" "}
                              {ppm} ref. pg/min
                            </div>
                            {(book.highlights > 0 || book.notes > 0) && (
                              <div
                                style={{
                                  fontSize: "0.85rem",
                                  color: C.dim,
                                  marginTop: "5px",
                                }}
                              >
                                💡 {book.highlights} highlights • 📝{" "}
                                {book.notes} notes
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
            {selectedBook && (
              <Row className="mt-4">
                <Col md={12}>
                  <Card
                    className="chart-card"
                    style={{ border: "2px solid rgba(63, 81, 181, 1)" }}
                  >
                    <Card.Body>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          marginBottom: "15px",
                        }}
                      >
                        <Card.Title>📖 {selectedBook.title}</Card.Title>
                        <button
                          onClick={() => setSelectedBook(null)}
                          style={{
                            background: "none",
                            border: "none",
                            fontSize: "1.5rem",
                            cursor: "pointer",
                            color: C.fallback,
                          }}
                        >
                          ×
                        </button>
                      </div>

                      <div style={{ padding: "15px 0" }}>
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns:
                              "repeat(auto-fit, minmax(150px, 1fr))",
                            gap: "15px",
                            marginBottom: "20px",
                          }}
                        >
                          <div
                            className="stat-box"
                            style={{
                              textAlign: "center",
                              padding: "15px",
                              backgroundColor: "rgba(76, 175, 80, 0.2)",
                              borderRadius: "8px",
                              border: "1px solid rgba(76, 175, 80, 0.3)",
                            }}
                          >
                            <div
                              style={{
                                fontSize: "1.5rem",
                                fontWeight: "bold",
                                color: C.green,
                              }}
                            >
                              {completionPct(selectedBook)}%
                            </div>
                            <div style={{ fontSize: "0.85rem", color: C.muted }}>
                              Complete
                            </div>
                          </div>
                          <div
                            className="stat-box"
                            style={{
                              textAlign: "center",
                              padding: "15px",
                              backgroundColor: "rgba(33, 150, 243, 0.2)",
                              borderRadius: "8px",
                              border: "1px solid rgba(33, 150, 243, 0.3)",
                            }}
                          >
                            <div
                              style={{
                                fontSize: "1.5rem",
                                fontWeight: "bold",
                                color: C.blue,
                              }}
                            >
                              {Math.round(
                                (selectedBook.timeSeconds / 3600) * 10
                              ) / 10}
                              h
                            </div>
                            <div style={{ fontSize: "0.85rem", color: C.muted }}>
                              Time Spent
                            </div>
                          </div>
                          <div
                            className="stat-box"
                            style={{
                              textAlign: "center",
                              padding: "15px",
                              backgroundColor: "rgba(255, 152, 0, 0.2)",
                              borderRadius: "8px",
                              border: "1px solid rgba(255, 152, 0, 0.3)",
                            }}
                          >
                            <div
                              style={{
                                fontSize: "1.5rem",
                                fontWeight: "bold",
                                color: C.orange,
                              }}
                            >
                              {selectedBook.timeSeconds > 0
                                ? Math.round(
                                    (selectedBook.pagesRead /
                                      (selectedBook.timeSeconds / 60)) *
                                      10
                                  ) / 10
                                : 0}
                            </div>
                            <div style={{ fontSize: "0.85rem", color: C.muted }}>
                              Ref. Pages/Min
                            </div>
                          </div>
                          <div
                            className="stat-box"
                            style={{
                              textAlign: "center",
                              padding: "15px",
                              backgroundColor: "rgba(156, 39, 176, 0.2)",
                              borderRadius: "8px",
                              border: "1px solid rgba(156, 39, 176, 0.3)",
                            }}
                          >
                            <div
                              style={{
                                fontSize: "1.5rem",
                                fontWeight: "bold",
                                color: C.purple,
                              }}
                            >
                              {selectedBook.highlights + selectedBook.notes}
                            </div>
                            <div style={{ fontSize: "0.85rem", color: C.muted }}>
                              Interactions
                            </div>
                          </div>
                        </div>

                        <div
                          style={{
                            backgroundColor: C.hover,
                            padding: "15px",
                            borderRadius: "8px",
                            marginBottom: "15px",
                            border: `1px solid ${C.grid}`,
                            color: C.text,
                          }}
                        >
                          <div style={{ marginBottom: "10px" }}>
                            <strong>Author:</strong> {selectedBook.author}
                          </div>
                          <div style={{ marginBottom: "10px" }}>
                            <strong>Progress:</strong> {selectedBook.pagesRead}{" "}
                            / {selectedBook.totalPages} ref. pages
                          </div>
                          <div style={{ marginBottom: "10px" }}>
                            <strong>Last Opened:</strong>{" "}
                            {new Date(
                              selectedBook.lastOpen * 1000
                            ).toLocaleDateString()}
                          </div>
                          <div style={{ marginBottom: "10px" }}>
                            <strong>Estimated Time to Finish:</strong>{" "}
                            {/* Pages left come from how far in you are; the rate comes from
                                pages actually logged per hour. */}
                            {selectedBook.timeSeconds > 0 &&
                            selectedBook.pagesRead > 0 &&
                            completionPct(selectedBook) < 100
                              ? Math.round(
                                  ((selectedBook.totalPages -
                                    Math.max(selectedBook.currentPage || 0, 0)) /
                                    (selectedBook.pagesRead /
                                      (selectedBook.timeSeconds / 3600))) *
                                    10
                                ) /
                                  10 +
                                " hours"
                              : "Completed or no data"}
                          </div>
                        </div>

                        {(selectedBook.highlights > 0 ||
                          selectedBook.notes > 0) && (
                          <div
                            style={{
                              backgroundColor: "rgba(255, 193, 7, 0.2)",
                              padding: "15px",
                              borderRadius: "8px",
                              borderLeft: "4px solid #ffc107",
                              border: "1px solid rgba(255, 193, 7, 0.3)",
                              color: C.text,
                            }}
                          >
                            <div
                              style={{
                                fontWeight: "bold",
                                marginBottom: "10px",
                              }}
                            >
                              📝 Engagement
                            </div>
                            <div>
                              💡 <strong>{selectedBook.highlights}</strong>{" "}
                              highlights
                            </div>
                            <div>
                              📝 <strong>{selectedBook.notes}</strong> notes
                            </div>
                            <div
                              style={{
                                marginTop: "10px",
                                fontSize: "0.9rem",
                                color: C.muted,
                              }}
                            >
                              Engagement rate:{" "}
                              {selectedBook.totalPages > 0
                                ? (
                                    ((selectedBook.highlights +
                                      selectedBook.notes) /
                                      selectedBook.totalPages) *
                                    100
                                  ).toFixed(2)
                                : 0}
                              % per ref. page
                            </div>
                          </div>
                        )}
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            )}
          </>
        );

      default:
        return (
          <div style={{ color: C.text, padding: "20px" }}>
            Select a chart from above
          </div>
        );
    }
  };

  return (
    <LayoutContainer>
      <Helmet>
        <title>{`Reading Statistics - ${SITE_NAME}`}</title>
        <meta
          name="description"
          content="My reading statistics and book tracking data"
        />
      </Helmet>

      <Container style={{ paddingBottom: "50px" }}>
        {/* Header */}
        <Row className="mb-4">
          <Col>
            <h1
              style={{ color: "black", textAlign: "center", marginTop: "20px" }}
            > My Complete Reading Statistics and Book Tracking Data
            </h1>
            <p style={{ textAlign: "center", color: C.muted, fontSize: "0.85rem" }}>
              "Ref. pages" are rebased to each book's own page count, so reading on different screens and font sizes is comparable.
            </p>
          </Col>
        </Row>

        {/* Sidebar Layout */}
        <Row style={{ minHeight: "80vh" }}>
          {/* Left Sidebar - Collapsible */}
          {!isSidebarCollapsed && (
            <Col md={2} style={{ backgroundColor: C.panel, borderRight: `1px solid ${C.line}`, padding: "20px 15px", borderRadius: "8px", transition: "all 0.3s" }}>
              <div style={{ position: "sticky", top: "20px" }}>
                {/* Collapse Button */}
                <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "15px" }}>
                  <button
                    onClick={() => setIsSidebarCollapsed(true)}
                    style={{
                      background: C.grid,
                      border: `1px solid ${C.line}`,
                      borderRadius: "4px",
                      padding: "5px 10px",
                      color: C.text,
                      cursor: "pointer",
                      fontSize: "0.85rem",
                      transition: "all 0.2s",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = C.line;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = C.grid;
                    }}
                  >
                    ◀ Hide
                  </button>
                </div>
              {readerSelector}
              {[
                { label: "📊 Overview", items: [
                  { id: "summary", title: "Summary" },
                  { id: "insights", title: "Insights" },
                ]},
                { label: "🔬 Deep Analysis", items: [
                  { id: "sessionCap", title: "Session Cap" },
                  { id: "bursts", title: "Bursts" },
                  { id: "revisited", title: "Revisited" },
                  { id: "dominance", title: "Dominance" },
                  { id: "dataQuality", title: "Quality" },
                ]},
                { label: "📅 Temporal Trends", items: [
                  { id: "paceOverTime", title: "Pace Trends" },
                  { id: "engagementScatter", title: "Eng vs Pace" },
                  { id: "timeHeatmap", title: "Time Heat" },
                  { id: "heatmapHours", title: "Hours" },
                  { id: "heatmapPages", title: "Ref. Pages" },
                ]},
                { id: "patterns", label: "🎯 Patterns", items: [
                  { id: "engagement", title: "Engagement" },
                  { id: "circadian", title: "Circadian" },
                  { id: "momentum", title: "Momentum" },
                  { id: "clusters", title: "Clusters" },
                  { id: "temporal", title: "Habits" },
                  { id: "authorStyle", title: "Styles" },
                  { id: "anomalies", title: "Anomalies" },
                ]},
                { label: "📚 Views", items: [
                  { id: "weekly", title: "Weekly" },
                  { id: "authors", title: "Authors" },
                  { id: "dayOfWeek", title: "By Day" },
                  { id: "bubble", title: "Patterns" },
                  { id: "longest", title: "Longest" },
                  { id: "timeInvested", title: "Time" },
                  { id: "streaks", title: "Streaks" },
                  { id: "speed", title: "Speed" },
                  { id: "yearly", title: "Yearly" },
                  { id: "allBooks", title: "All Books" },
                ]},
              ].map((section, idx) => (
                <div key={idx} style={{ marginBottom: "20px" }}>
                  <div style={{ fontSize: "0.75rem", color: C.dim, fontWeight: "bold", marginBottom: "8px", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                    {section.label}
                  </div>
                  {section.items.map((item) => (
                    <div
                      key={item.id}
                      onClick={() => {
                        setSelectedChart(item.id);
                        setTimeout(() => {
                          document.getElementById('chart-display')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }, 100);
                      }}
                      style={{
                        padding: "8px 10px",
                        marginBottom: "4px",
                        fontSize: "0.85rem",
                        color: selectedChart === item.id ? C.accent : C.text,
                        backgroundColor: selectedChart === item.id ? C.accentBg : "transparent",
                        borderLeft: selectedChart === item.id ? "3px solid #007bff" : "3px solid transparent",
                        cursor: "pointer",
                        borderRadius: "4px",
                        transition: "all 0.2s",
                      }}
                      onMouseEnter={(e) => {
                        if (selectedChart !== item.id) {
                          e.currentTarget.style.backgroundColor = C.hover;
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (selectedChart !== item.id) {
                          e.currentTarget.style.backgroundColor = "transparent";
                        }
                      }}
                    >
                      {item.title}
                    </div>
                  ))}
                </div>
              ))}
              </div>
            </Col>
          )}

          {/* Expand Button - Shows when sidebar is collapsed */}
          {isSidebarCollapsed && (
            <div style={{ 
              position: "fixed", 
              left: "20px", 
              top: "50%", 
              transform: "translateY(-50%)",
              zIndex: 1000,
            }}>
              <button
                onClick={() => setIsSidebarCollapsed(false)}
                style={{
                  background: C.btn,
                  border: `1px solid ${C.line}`,
                  borderRadius: "4px",
                  padding: "10px 8px",
                  color: C.text,
                  cursor: "pointer",
                  fontSize: "1rem",
                  transition: "all 0.2s",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = C.btnHover;
                  e.currentTarget.style.transform = "scale(1.1)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = C.btn;
                  e.currentTarget.style.transform = "scale(1)";
                }}
                title="Show Statistics Menu"
              >
                ▶
              </button>
            </div>
          )}

          {/* Right Content Area - Adjusts width based on sidebar state */}
          <Col md={isSidebarCollapsed ? 12 : 10} id="chart-display" style={{ transition: "all 0.3s" }}>
            {renderSelectedChart()}
          </Col>
        </Row>

        {/* Back Home Button */}
        <BackHome variant="outline-light" />
      </Container>
    </LayoutContainer>
  );
};

export default ReadingStats;
